import { bindLocalDatabaseEpoch } from '@/services/db/LocalDatabaseEpoch';
import type { RetainedChecklistUploadInput, StructuralMutationGuard } from './IDatabaseAdapter';
import { CHECKLIST_DELETION_TABLES, deleteChecklistInTransaction, type ChecklistDeletionInput } from '@/services/checklist/ChecklistDeletionTransaction';
import { planSupersededMediaNoteConflict, captureChecklistMediaSuccession } from '@/services/sync/ChecklistMediaSuccessionContract';
import { hasMediaCancellation, hasOpenMediaObservation, hasPendingMediaObservation, planChecklistMediaConflictCommit, type ChecklistMediaConflictCommit } from '@/services/sync/ChecklistMediaConflictContract';
import { remoteWouldReplacePendingMedia, checklistObservationMediaVersion, canAdvanceMediaObservationBaseline, type ChecklistMediaObservationBaselineInput } from '@/services/sync/ChecklistMediaObservationContract';
import { planChecklistMediaCanonicalAdoption, type ChecklistMediaCanonicalAdoptionInput } from '@/services/sync/ChecklistMediaCanonicalAdoptionContract';
import { mergeChecklistAnswerChanges, mergeChecklistAnswerValuesFromRows } from '@/services/checklist/checklistObservations';
import { historicalInsertGuardMatches, type HistoricalInsertGuard } from '../services/sync/HistoricalInsertGuard';
import { commitArchivedPartialReplay, type ArchivedReplayCommit } from '@/services/sync/ArchivedPartialReplayCoordinator';
import type { ChecklistMediaFinalizationInput } from './IDatabaseAdapter';
import Dexie from 'dexie';
import { retainAcknowledgedLocalMedia, planDeferredMediaCleanup, assertSameCleanupIntent, DEFERRED_MEDIA_CLEANUP_TABLE } from '@/services/checklist/DeferredMediaCleanup';
import { Platform } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import {
  assertChecklistMediaPendingInput,
  captureChecklistMediaBaseline,
  planAcknowledgedMediaPartial,
  type CanonicalMetadataCandidateScope,
  type ChecklistMediaCompletionInput,
  type ChecklistMediaPendingInput,
  type PendingQueueOrigin,
  type ChecklistSnapshotAtomicCreateInput,
  type ChecklistSnapshotCanonicalReconciliationInput,
  type ChecklistStructuralMutationInput,
  type ChecklistStructuralMutationResult,
  type DatabaseMutationInput,
  IDatabaseAdapter,
  RECOVERY_LOCAL_TABLE_NAMES,
  FULL_STATE_LOCAL_TABLE_NAMES,
  scopeRecoverySnapshot,
  scopeFullStateSnapshot,
  resolveDatabaseMutationOptions,
} from './IDatabaseAdapter';
import { databaseActions } from '../state/globalState';
import { capturePendingWorkOrigin, type PendingWorkOrigin } from '@/services/sync/PendingSyncSession';
import { APP_CONSTANTS } from '../config/constants';
import { DexieAdapterLogger } from '../utils/logger';
import type { PendingOperationRaw } from '../services/syncService';
import { schedulePushAfterPending } from '../services/syncScheduler';
import { debugPause } from '@/utils/debugPauseUniversal';
import type {
  RecoveryCaptureInput,
  RecoveryConsistentSnapshot,
  RecoverySnapshotRow,
  RecoverySnapshotTableName,
} from '@/types/recovery';
import type { SupportFullStateSnapshot, SupportFullStateRow } from '@/types/supportFullState';

// Loggers específicos para operações do DexieAdapter
const logger = DexieAdapterLogger.Main;
const initLogger = DexieAdapterLogger.Initialize;
const createTableLogger = DexieAdapterLogger.CreateTables;
const bulkUpsertLogger = DexieAdapterLogger.BulkUpsert;
const createItemLogger = DexieAdapterLogger.CreateItem;
const readItemLogger = DexieAdapterLogger.ReadItem;
const updateItemLogger = DexieAdapterLogger.UpdateItem;
const deleteItemLogger = DexieAdapterLogger.DeleteItem;
const listItemsLogger = DexieAdapterLogger.ListItems;
const registerPendingOpLogger = DexieAdapterLogger.RegisterPendingOp;
const getPendingOpsLogger = DexieAdapterLogger.GetPendingOps;
const removePendingOpLogger = DexieAdapterLogger.RemovePendingOp;
const closeLogger = DexieAdapterLogger.Close;
export const DEXIE_DATABASE_VERSION = 6;

type StructuralPendingChange = 'inserted' | 'updated' | 'unchanged';

const DEXIE_V2_TABLES = new Set([
  'local_database_control',
  'deferred_media_cleanup',
  'sync_conflicts',
  'sync_tombstones',
  'sync_cursors',
  'accounted_pending_operations',
  'guincho_template_schema_versions',
  'guincho_checklist_snapshots',
  'guincho_checklist_snapshot_fields',
]);

const DEXIE_V2_COLUMNS = new Set(['retired_at', 'retired_by', 'snapshot_field_id', 'expected_partner_revision']);

export const buildLegacyDexieStoreDefinitions = (
  currentDefinitions: Record<string, string>
): Record<string, string> => Object.fromEntries(
  Object.entries(currentDefinitions)
    .filter(([tableName]) => !DEXIE_V2_TABLES.has(tableName))
    .map(([tableName, definition]) => [
      tableName,
      definition
        .split(', ')
        .filter((token) => !DEXIE_V2_COLUMNS.has(token))
        .join(', '),
    ])
);

// Interface dinâmica para tabelas definidas a partir de APP_CONSTANTS
interface IAppDatabaseTables {
  pending_operations: Dexie.Table<{
    id: string;
    operation: string;
    collection: string;
    item_id: string;
    data?: string;
    timestamp: number;
    user_id?: string | null;
    empresa_id?: string | null;
  }, string>;
  [key: string]: Dexie.Table<any, string>; // Para tabelas dinâmicas adicionais
}

// Extensão da classe Dexie com tipos para tabelas dinâmicas
class AppDatabase extends Dexie implements IAppDatabaseTables {
  pending_operations!: Dexie.Table<{
    id: string;
    operation: string;
    collection: string;
    item_id: string;
    data?: string;
    timestamp: number;
    user_id?: string | null;
    empresa_id?: string | null;
  }, string>;
  [key: string]: any; // Para tabelas dinâmicas adicionais

  constructor(dbName: string) {
    super(dbName);

    // Mapear as definições de tabelas do APP_CONSTANTS para o formato esperado pelo Dexie
    const storeDefinitions: Record<string, string> = {};

    // Obter as definições de tabelas do APP_CONSTANTS
    const tables = APP_CONSTANTS.DATABASE.TABLES;

    const schemaDefinitionSummary: string[] = [];

    // Processar todas as tabelas definidas em APP_CONSTANTS
    Object.keys(tables).forEach(tableKey => {
      const tableDef = tables[tableKey as keyof typeof tables];

      // Extrair nome da tabela
      const tableName = tableDef.NAME;

      // Identificar a chave primária
      const primaryKey = tableDef.COLUMNS.find(col => col.primaryKey)?.name || 'id';

      // Construir lista de índices a partir das colunas (excluindo a chave primária)
      const indexColumns = tableDef.COLUMNS
        .filter(col => col.name !== primaryKey && !col.name.includes(' '))
        .map(col => col.name);

      // Adicionar índices compostos, se existirem
      if (tableDef.INDEXES) {
        tableDef.INDEXES.forEach((indexDef: any) => {
          if (Array.isArray(indexDef.columns) && indexDef.columns.length > 1) {
            // Para índice composto, no Dexie usamos a notação [col1+col2]
            const compositeIndex = `[${indexDef.columns.join('+')}]`;
            if (!indexColumns.includes(compositeIndex)) {
              indexColumns.push(compositeIndex);
            }
          } else if (Array.isArray(indexDef.columns) && indexDef.columns.length === 1) {
            // Para índice simples, adicionar a coluna se ainda não estiver na lista
            const singleColumn = indexDef.columns[0];
            if (!indexColumns.includes(singleColumn)) {
              indexColumns.push(singleColumn);
            }
          }
        });
      }

      // Construir definição da tabela com chave primária e índices
      storeDefinitions[tableName] = indexColumns.length > 0
        ? `${primaryKey}, ${indexColumns.join(', ')}`
        : primaryKey;

      schemaDefinitionSummary.push(`${tableName}: ${storeDefinitions[tableName]}`);
    });

    /*
    if (schemaDefinitionSummary.length > 0) {
      createTableLogger.info(
        `Schema Dexie configurado (${schemaDefinitionSummary.length} tabelas):\n${schemaDefinitionSummary.join('\n')}`
      );
    }
      */

    // A versao 1 reproduz o schema anterior. A versao 2 adiciona stores e
    // indices sem limpar ou reescrever os registros existentes.
    this.version(1).stores(buildLegacyDexieStoreDefinitions(storeDefinitions));
    this.version(DEXIE_DATABASE_VERSION).stores(storeDefinitions);

    // Definir propriedades para tabelas específicas dinamicamente
    Object.keys(tables).forEach(tableKey => {
      const tableDef = tables[tableKey as keyof typeof tables];
      const tableName = tableDef.NAME;
      this[tableName] = this.table(tableName);
    });
  }
}

/**
 * Remove todos os campos com sufixo "local_path" de um objeto
 * @param data Objeto a ser limpo
 * @returns Objeto sem campos "local_path"
 */
function removeLocalPathFields(data: any): any {
  if (!data) return data;

  // Se for string (JSON), converter para objeto
  let objData = data;
  if (typeof data === 'string') {
    try {
      objData = JSON.parse(data);
    } catch (e) {
      return data; // Se não puder ser analisado, retornar como está
    }
  }

  // Criar cópia para não modificar o original
  const cleanData = { ...objData };

  // Remover campos com "local_path"
  Object.keys(cleanData).forEach(key => {
    if (key.includes('local_path')) {
      delete cleanData[key];
    }
  });

  // Se o input original era string, retornar como string
  return typeof data === 'string' ? JSON.stringify(cleanData) : cleanData;
}

/**
 * Adaptador para banco de dados Dexie (IndexedDB)
 * Implementa a interface IDatabaseAdapter para operações CRUD no navegador
 */
export class DexieAdapter implements IDatabaseAdapter {
  private db: AppDatabase | null = null;
  private userId: string | null = null;
  private dbName: string = 'app_database';
  private ready: boolean = false;
  private boundEpoch = 0;

  private assertIdempotentRecord(
    collection: string,
    expected: Record<string, unknown>,
    current: Record<string, unknown>
  ): void {
    for (const [key, value] of Object.entries(expected)) {
      const normalizedExpected = value === undefined ? null : value;
      const normalizedActual = current[key] === undefined ? null : current[key];
      if (normalizedExpected !== normalizedActual) {
        throw new Error(`Conflito local em ${collection}/${String(expected.id)}; transacao recusada.`);
      }
    }
  }

  private recordHasLocalChange(
    current: Record<string, unknown>,
    expected: Record<string, unknown>
  ): boolean {
    return Object.entries(expected).some(([key, value]) => {
      const expectedValue = value === undefined ? null : value;
      const currentValue = current[key] === undefined ? null : current[key];
      return expectedValue !== currentValue;
    });
  }

  private async upsertStructuralPending(
    pendingTable: Dexie.Table<any, string>,
    operation: 'create' | 'update' | 'delete' | 'checklist_partial_update',
    collection: string,
    itemId: string,
    data: Record<string, unknown>,
    timestamp: number,
    origin: PendingQueueOrigin | null = capturePendingWorkOrigin()
  ): Promise<StructuralPendingChange> {
    const dataJson = JSON.stringify(data);
    const existing = origin
      ? (await pendingTable.toArray()).find(row =>
          row.operation === operation && row.collection === collection && row.item_id === itemId &&
          row.user_id === origin.userId && row.empresa_id === origin.empresaId)
      : null;

    if (existing) {
      if (existing.data === dataJson) return 'unchanged';
      if (operation !== 'update' && operation !== 'checklist_partial_update') {
        throw new Error(`Pendencia ${operation} existente diverge para ${collection}/${itemId}.`);
      }

      let mergedDataJson = dataJson;
      try {
        const existingData = existing.data ? JSON.parse(existing.data) : null;
        const nextData = JSON.parse(dataJson);
        if (
          existingData && typeof existingData === 'object' && !Array.isArray(existingData) &&
          nextData && typeof nextData === 'object' && !Array.isArray(nextData)
        ) {
          mergedDataJson = JSON.stringify(operation === 'checklist_partial_update' ? mergeChecklistAnswerChanges(existingData, nextData) : { ...existingData, ...nextData });
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'partner_pending_revision_mismatch') throw error;
        mergedDataJson = dataJson;
      }

      if (existing.data === mergedDataJson) return 'unchanged';

      await pendingTable.update(existing.id, {
        data: mergedDataJson,
        timestamp,
      });
      return 'updated';
    }

    await pendingTable.add({
      id: uuidv4(),
      operation,
      collection,
      item_id: itemId,
      data: dataJson,
      timestamp,
      user_id: origin?.userId ?? null,
      empresa_id: origin?.empresaId ?? null,
    });
    return 'inserted';
  }

  private async putRecordIdempotently(
    table: Dexie.Table<any, string>,
    collection: string,
    record: Record<string, unknown>
  ): Promise<boolean> {
    const id = record.id;
    if (typeof id !== 'string' || id.length === 0) {
      throw new Error(`Registro sem id para transacao atomica em ${collection}.`);
    }
    const existing = await table.get(id);
    if (existing) {
      this.assertIdempotentRecord(collection, record, existing as Record<string, unknown>);
      return false;
    }
    await table.add(record as any);
    return true;
  }

  /**
   * Atualiza o ID do usuário no adaptador — útil para setar userId após o login
   */
  setUserId(userId: string): void {
    this.userId = userId;
    initLogger.info(`[DexieAdapter] userId atualizado para: ${userId}`);
  }

  /**
   * Inicializa o banco de dados Dexie
   * @param userId ID do usuário atual (opcional)
   */
  async initialize(userId?: string): Promise<void> {
    try {
      initLogger.info('Inicializando adaptador Dexie...');

      // Se já estiver pronto, só atualiza o userId (se fornecido) e retorna
      if (this.ready) {
        if (userId) {
          initLogger.info('[DexieAdapter] já inicializado — atualizando userId');
          this.userId = userId;
        } else {
          initLogger.warn('[DexieAdapter] já inicializado — sem userId para atualizar');
        }
        return;
      }

      // Verificar se estamos em uma plataforma compatível
      if (Platform.OS !== 'web') {
        initLogger.error('DexieAdapter não é suportado no ambiente mobile');
        throw new Error('DexieAdapter não é suportado no ambiente mobile. Use SQLiteAdapter.');
      }

      // Guardar o userId para uso posterior
      this.userId = userId || null;
      initLogger.info(`UserID definido: ${this.userId || 'não fornecido'}`);

      // Criar instância do banco de dados
      this.db = new AppDatabase(this.dbName);
      initLogger.info('Banco de dados Dexie aberto com sucesso');

      // Verificar estrutura do banco de dados
      await this.checkDatabaseSchema();

      const control = await this.db.table('local_database_control').get('generation');
      this.boundEpoch = Number(control?.epoch ?? 0);
      bindLocalDatabaseEpoch(this.dbName, this.boundEpoch, async () => Number((await this.db!.table('local_database_control').get('generation'))?.epoch ?? 0));
      // Marcar como pronto
      this.ready = true;
      databaseActions.setInitialized(true, 'dexie');

      initLogger.info('Adaptador Dexie inicializado com sucesso');
    } catch (error) {
      initLogger.error('Erro ao inicializar adaptador Dexie:', error);
      databaseActions.setError(`Erro ao inicializar Dexie: ${error}`);
      throw error;
    }
  }

  /**
   * Verifica o schema do banco de dados
   */
  private async checkDatabaseSchema(): Promise<void> {
    try {
      createTableLogger.info('Verificando schema do banco de dados Dexie...');

      if (!this.db) {
        throw new Error('Banco de dados não inicializado');
      }

      const formatKeyPath = (keyPath: unknown): string => {
        if (Array.isArray(keyPath)) {
          return keyPath.join('+');
        }
        if (typeof keyPath === 'string' && keyPath.length > 0) {
          return keyPath;
        }
        return 'desconhecida';
      };

      const tables = this.db.tables.map(table => table.name);
      const resumoPorTabela: string[] = [];

      for (const table of this.db.tables) {
        const tableSchema = table.schema;
        const primaryKey = tableSchema ? formatKeyPath(tableSchema.primKey.keyPath) : 'desconhecida';
        const indexList = tableSchema
          ? tableSchema.indexes.map(idx => {
            const formattedKeyPath = formatKeyPath((idx as any).keyPath);
            return idx.name ? `${idx.name}: ${formattedKeyPath}` : formattedKeyPath;
          })
          : [];

        let registrosDescricao = '0';
        try {
          const count = await table.count();
          registrosDescricao = `${count}`;
        } catch (countError) {
          const errorMessage = countError instanceof Error ? countError.message : String(countError);
          registrosDescricao = `erro ao contar (${errorMessage})`;
        }

        const indicesTexto = indexList.length > 0 ? indexList.join(', ') : 'nenhum índice';
        resumoPorTabela.push(
          `- ${table.name} | PK: ${primaryKey} | Índices: ${indicesTexto} | Registros: ${registrosDescricao}`
        );
      }

      const tablesFromConstants = Object.values(APP_CONSTANTS.DATABASE.TABLES).map(
        (tableDef: any) => tableDef.NAME
      );
      const missingTables = tablesFromConstants.filter(
        tableName => !tables.includes(tableName)
      );

      let mensagemResumo = resumoPorTabela.length > 0
        ? `Resumo do schema Dexie (${resumoPorTabela.length} tabelas registradas):\n${resumoPorTabela.join('\n')}`
        : 'Resumo do schema Dexie: nenhuma tabela encontrada no IndexedDB';

      if (missingTables.length > 0) {
        mensagemResumo += `\nATENÇÃO: tabelas definidas em APP_CONSTANTS ausentes no IndexedDB: ${missingTables.join(', ')}`;
      }

      createTableLogger.info(mensagemResumo);

    } catch (error) {
      createTableLogger.error('Erro ao verificar schema do banco de dados:', error);
      throw error;
    }
  }

  /**
   * Valida se o adaptador está pronto para uso
   */
  private validateReady(): void {
    if (!this.ready || !this.db) {
      logger.warn('Adaptador Dexie não inicializado');
      throw new Error('Adaptador Dexie não inicializado. Chame initialize() primeiro.');
    }
  }

  /**
   * Obtém a tabela correspondente a uma coleção
   * @param collection Nome da coleção
   * @returns Tabela Dexie ou null se não encontrada
   */
  private getTable(collection: string): Dexie.Table<any, string> | null {
    if (!this.db) return null;

    // Verificar se é uma tabela válida
    if (this.db[collection]) {
      return this.db[collection];
    }

    logger.warn(`Tabela "${collection}" não encontrada no schema`);
    return null;
  }

  /** O epoch fica preso à conexão: uma resposta anterior ao reset não pode escrever depois. */
  private runTransaction<T>(mode: 'r' | 'rw', tables: Dexie.Table<any, any> | Dexie.Table<any, any>[], action: () => Promise<T>): Promise<T> {
    const stores = Array.isArray(tables) ? tables : [tables];
    if (mode === 'r') return this.db!.transaction(mode, stores, action);
    const control = this.db!.table('local_database_control');
    return this.db!.transaction('rw', [...new Set([...stores, control])], async () => {
      const epoch = Number((await control.get('generation'))?.epoch ?? 0);
      if (epoch !== this.boundEpoch) throw new Error('database_reset_restart_required');
      return action();
    });
  }

  private async runMutationTransaction<T>(
    table: Dexie.Table<any, string>,
    action: () => Promise<T>
  ): Promise<T> {
    const stores = table.name === this.db!.pending_operations.name
      ? [table]
      : [table, this.db!.pending_operations];
    return this.runTransaction('rw', stores, action);
  }

  /**
   * Verifica se uma coluna existe na definição da tabela
   * @param collection Nome da coleção (tabela)
   * @param columnName Nome da coluna a verificar
   * @returns Booleano indicando se a coluna existe
   */
  private columnExistsInTable(collection: string, columnName: string): boolean {
    // Obter a definição da tabela
    const tableDefinitions = APP_CONSTANTS.DATABASE.TABLES;
    const tableDef = Object.values(tableDefinitions).find(def => def.NAME === collection);

    if (!tableDef) {
      return false;
    }

    // Verificar se a coluna existe na definição
    return tableDef.COLUMNS.some(col => col.name === columnName);
  }

  /**
   * Cria um novo item na coleção especificada
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param item Item a ser criado
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns Promise com o ID do item criado
   */
  async createItem<T>(collection: string, item: T, options: DatabaseMutationInput = false): Promise<string> {
    let attemptedId: string | undefined;
    try {
      this.validateReady();
      const pendingOrigin = capturePendingWorkOrigin();

      const mutation = resolveDatabaseMutationOptions(options);
      const skipPendingOperation = mutation.skipPendingOperation;
      const remoteWrite = skipPendingOperation && mutation.source === 'remote';

      if (!item || typeof item !== 'object') {
        throw new Error(`Item inválido para criação na coleção ${collection}`);
      }

      // Gerar ID se não existir
      const itemWithId = {
        ...item as object,
        id: (item as any).id || uuidv4()
      };
      const id = itemWithId.id;
      attemptedId = id;

      createItemLogger.debug(`Criando item na coleção "${collection}" com ID "${id}"`);

      // Obter a tabela correspondente
      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      // Preparar o item para armazenamento com timestamps
      const now = Date.now();

      // Criar objeto básico com id e timestamps
      const fullItem: any = {
        ...itemWithId,
        created_at: (itemWithId as any).created_at || now,
        updated_at: (itemWithId as any).updated_at || now,
      };

      // Apenas adicionar user_id se a coluna existir na definição da tabela
      if (this.columnExistsInTable(collection, 'user_id') && this.userId) {
        fullItem.user_id = this.userId;
      }

      if (remoteWrite) {
        await this.runMutationTransaction(table, async () => { await table.add(fullItem); });
      } else {
        await this.runMutationTransaction(table, async () => {
          await table.add(fullItem);
          if (!skipPendingOperation) {
            await this.registerPendingOperation('create', collection, id, JSON.stringify(fullItem), pendingOrigin);
          }
        });
      }

      if (skipPendingOperation) {
        createItemLogger.debug(`Pulando registro de operação pendente para item ${collection}/${id}`);
      }

      createItemLogger.info(`Item criado com sucesso na coleção "${collection}" com ID "${id}"`);
      return id;
    } catch (error) {
      createItemLogger.error(`Erro ao criar item na coleção "${collection}" com ID "${attemptedId ?? 'não atribuído'}":`, error);
      throw error;
    }
  }

  /**
   * Lê um item específico de uma coleção
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param id ID do item
   * @returns Promise com o item encontrado ou null
   */
  async readItem<T>(collection: string, id: string): Promise<T | null> {
    try {
      this.validateReady();

      //readItemLogger.debug(`Lendo item da coleção "${collection}" com ID "${id}"`);

      // Obter a tabela correspondente
      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      // Buscar o item diretamente
      const item = await table.get(id);

      if (!item) {
        readItemLogger.debug(`Item não encontrado na coleção "${collection}" com ID "${id}"`);
        return null;
      }

      // Sucesso virou caminho silencioso para evitar ruído em leituras triviais.
      return item as unknown as T;
    } catch (error) {
      readItemLogger.error(`Erro ao ler item da coleção "${collection}" com ID "${id}":`, error);
      throw error;
    }
  }

  /**
   * Atualiza um item existente em uma coleção
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param id ID do item
   * @param updates Atualizações parciais
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns Promise com boolean indicando sucesso
   */
  async updateItem<T>(collection: string, id: string, updates: Partial<T>, options: DatabaseMutationInput = false): Promise<boolean> {
    try {
      this.validateReady();
      const pendingOrigin = capturePendingWorkOrigin();

      const mutation = resolveDatabaseMutationOptions(options);
      const skipPendingOperation = mutation.skipPendingOperation;
      const remoteWrite = skipPendingOperation && mutation.source === 'remote';

      updateItemLogger.debug(`Atualizando item na coleção "${collection}" com ID "${id}"`);

      // Obter a tabela correspondente
      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      // Verificar se o item existe
      const item = await table.get(id);
      if (!item) {
        updateItemLogger.warn(`Item não encontrado para atualização na coleção "${collection}" com ID "${id}"`);
        return false;
      }

      const now = Date.now();

      // Verificar se há campo user_id no updates e removê-lo se não estiver definido na tabela
      const updatedItem = { ...updates } as any;

      // Remover o campo user_id se ele existir no updates mas não na definição da tabela
      if (updatedItem.user_id !== undefined && !this.columnExistsInTable(collection, 'user_id')) {
        updateItemLogger.debug(`Removendo campo user_id do update para a tabela ${collection} que não possui esta coluna`);
        delete updatedItem.user_id;
      }

      if (this.columnExistsInTable(collection, 'updated_at') && !remoteWrite) {
        updatedItem.updated_at = now;
        updateItemLogger.info(`DexieAdapter - Definindo updated_at como timestamp numérico: ${now} (${new Date(now).toISOString()})`);
      }

      const applyUpdate = async () => {
        const affectedRows = await table.update(id, updatedItem);
        if (affectedRows === 0) {
          throw new Error(`Item ${collection}/${id} desapareceu durante a atualização.`);
        }

        if (!skipPendingOperation) {
          const fullUpdatedItem = await table.get(id);
          const pendingItem = { ...fullUpdatedItem };
          if (pendingItem.user_id !== undefined && !this.columnExistsInTable(collection, 'user_id')) {
            delete pendingItem.user_id;
          }
          await this.registerPendingOperation('update', collection, id, JSON.stringify(pendingItem), pendingOrigin);
        }
      };

      if (remoteWrite) {
        await this.runMutationTransaction(table, applyUpdate);
      } else {
        await this.runMutationTransaction(table, applyUpdate);
      }

      if (skipPendingOperation) {
        updateItemLogger.debug(`Pulando registro de operação pendente para atualização ${collection}/${id}`);
      }

      updateItemLogger.info(`Item atualizado com sucesso na coleção "${collection}" com ID "${id}"`);
      return true;
    } catch (error) {
      updateItemLogger.error(`Erro ao atualizar item na coleção "${collection}" com ID "${id}":`, error);
      throw error;
    }
  }

  async retireItemAndPending(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    pendingData: Record<string, unknown>,
    guard?: StructuralMutationGuard
  ): Promise<boolean> {
    try {
      this.validateReady();
      const pendingOrigin = guard?.origin ?? capturePendingWorkOrigin();
      const assertScope = (row: Record<string, any>) => {
        const current = capturePendingWorkOrigin();
        if (!pendingOrigin || row.empresa_id !== pendingOrigin.empresaId || current?.userId !== pendingOrigin.userId || current?.empresaId !== pendingOrigin.empresaId || (guard && !guard.canCommit())) throw new Error('structural_origin_changed');
      };
      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      const existing = await table.get(id);
      if (!existing) {
        return false;
      }

      const updatedItem = {
        ...updates,
        updated_at: Date.now(),
      };
      const pendingDataJson = JSON.stringify(pendingData);
      let affectedRows = 0;
      let insertedPending = false;

      await this.runMutationTransaction(table, async () => {
        const current = await table.get(id);
        if (!current) throw new Error('structural_row_missing');
        assertScope(current);
        affectedRows = await table.update(id, updatedItem);
        if (affectedRows === 0) {
          throw new Error(`Item ${collection}/${id} desapareceu durante a aposentadoria.`);
        }

        const existingDelete = pendingOrigin
          ? (await this.db!.pending_operations.toArray()).find(row =>
              row.operation === 'delete' && row.collection === collection && row.item_id === id &&
              row.user_id === pendingOrigin.userId && row.empresa_id === pendingOrigin.empresaId)
          : null;
        if (existingDelete) {
          if (existingDelete.data !== pendingDataJson) {
            throw new Error(`Pendencia delete existente diverge para ${collection}/${id}.`);
          }
          assertScope(existing);
          return;
        }

        await this.db!.pending_operations.add({
          id: uuidv4(),
          operation: 'delete',
          collection,
          item_id: id,
          data: pendingDataJson,
          timestamp: Date.now(),
          user_id: pendingOrigin?.userId ?? null,
          empresa_id: pendingOrigin?.empresaId ?? null,
        });
        insertedPending = true;
        assertScope(current);
      });

      if (insertedPending) {
        databaseActions.incrementPendingChanges();
        try {
          schedulePushAfterPending(`${collection}:delete`);
        } catch (error) {
          registerPendingOpLogger.warn(
            `Falha ao agendar push apos aposentadoria atomica (${collection}/${id}):`,
            error
          );
        }
      }

      return affectedRows > 0;
    } catch (error) {
      updateItemLogger.error(`Erro ao aposentar item na coleção "${collection}":`, error);
      throw error;
    }
  }

  async applyChecklistStructuralMutation(
    input: ChecklistStructuralMutationInput
  ): Promise<ChecklistStructuralMutationResult> {
    try {
      this.validateReady();
      const pendingOrigin = input.guard?.origin ?? capturePendingWorkOrigin();
      const assertScope = (row: Record<string, any>) => {
        const current = capturePendingWorkOrigin();
        if (!pendingOrigin || row.empresa_id !== pendingOrigin.empresaId || current?.userId !== pendingOrigin.userId || current?.empresaId !== pendingOrigin.empresaId || (input.guard && !input.guard.canCommit())) throw new Error('structural_origin_changed');
      };
      if (!input.templateId || input.fieldMutations.length === 0) {
        throw new Error('Mutacao estrutural exige template e ao menos um campo.');
      }

      const templateCollection = APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME;
      const fieldCollection = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME;
      const templateTable = this.getTable(templateCollection);
      const fieldTable = this.getTable(fieldCollection);
      const pendingTable = this.db!.pending_operations;
      if (!templateTable || !fieldTable) {
        throw new Error('Tabelas estruturais nao encontradas no Dexie.');
      }

      const timestamp = Date.now();
      const reasons = new Set<string>();
      let insertedPendingCount = 0;
      let templateVersion = 0;
      let fieldResults: boolean[] = [];

      const markPending = (
        change: StructuralPendingChange,
        collection: string,
        operation: 'create' | 'update' | 'delete'
      ): void => {
        if (change === 'inserted') insertedPendingCount += 1;
        if (change !== 'unchanged') reasons.add(`${collection}:${operation}`);
      };

      await this.runTransaction(
        'rw',
        [templateTable, fieldTable, pendingTable],
        async () => {
          const template = await templateTable.get(input.templateId) as Record<string, unknown> | undefined;
          if (!template) {
            throw new Error(`Template ${input.templateId} nao encontrado para mutacao estrutural.`);
          }

          assertScope(template);
          const currentVersion = Number(template.versao ?? 1);
          if (!Number.isInteger(currentVersion) || currentVersion < 1) {
            throw new Error(`Versao estrutural invalida para o template ${input.templateId}.`);
          }

          templateVersion = currentVersion + input.fieldMutations.length;
          const templateChanges = await templateTable.update(input.templateId, {
            versao: templateVersion,
            updated_at: timestamp,
          });
          if (templateChanges === 0) {
            throw new Error(`Template ${input.templateId} desapareceu durante a mutacao estrutural.`);
          }

          markPending(
            await this.upsertStructuralPending(
              pendingTable,
              'update',
              templateCollection,
              input.templateId,
              { versao: templateVersion },
              timestamp,
              pendingOrigin
            ),
            templateCollection,
            'update'
          );

          fieldResults = [];
          for (const mutation of input.fieldMutations) {
            if (mutation.operation === 'create') {
              const requestedRecord: Record<string, unknown> = {
                ...mutation.record,
                id: mutation.record.id,
                template_id: mutation.record.template_id ?? input.templateId,
              };
              assertScope(requestedRecord);
              if (requestedRecord.template_id !== input.templateId) throw new Error('structural_template_mismatch');
              const fieldId = requestedRecord.id;
              if (typeof fieldId !== 'string' || fieldId.length === 0) {
                throw new Error('Campo estrutural sem id para criacao atomica.');
              }

              const existing = await fieldTable.get(fieldId) as Record<string, unknown> | undefined;
              let storedRecord: Record<string, unknown>;
              if (existing) {
                this.assertIdempotentRecord(fieldCollection, requestedRecord, existing);
                storedRecord = existing;
              } else {
                storedRecord = {
                  ...requestedRecord,
                  created_at: requestedRecord.created_at ?? timestamp,
                  updated_at: requestedRecord.updated_at ?? timestamp,
                };
                await fieldTable.add(storedRecord);
              }

              markPending(
                await this.upsertStructuralPending(
                  pendingTable,
                  'create',
                  fieldCollection,
                  fieldId,
                  storedRecord,
                  timestamp,
                  pendingOrigin
                ),
                fieldCollection,
                'create'
              );
              fieldResults.push(true);
              continue;
            }

            const fieldId = mutation.id;
            if (!fieldId) {
              throw new Error('Campo estrutural sem id para atualizacao atomica.');
            }
            const existing = await fieldTable.get(fieldId) as Record<string, unknown> | undefined;
            if (!existing) {
              throw new Error(`Campo ${fieldId} nao encontrado para mutacao estrutural.`);
            }

            assertScope(existing);
            if (existing.template_id !== input.templateId || (mutation.updates.template_id && mutation.updates.template_id !== input.templateId) || (mutation.updates.empresa_id && mutation.updates.empresa_id !== pendingOrigin!.empresaId)) throw new Error('structural_template_mismatch');
            const updatePayload = {
              ...mutation.updates,
              updated_at: timestamp,
            };
            const affectedRows = await fieldTable.update(fieldId, updatePayload);
            if (affectedRows === 0) {
              throw new Error(`Campo ${fieldId} desapareceu durante a mutacao estrutural.`);
            }

            const updatedRecord = await fieldTable.get(fieldId) as Record<string, unknown> ?? {
              ...existing,
              ...updatePayload,
            };
            const pendingData = mutation.operation === 'delete'
              ? mutation.pendingData
              : updatedRecord;
            markPending(
              await this.upsertStructuralPending(
                pendingTable,
                mutation.operation,
                fieldCollection,
                fieldId,
                pendingData,
                timestamp,
                pendingOrigin
              ),
              fieldCollection,
              mutation.operation
            );
            fieldResults.push(true);
          }
          assertScope(template);
        }
      );

      for (let index = 0; index < insertedPendingCount; index += 1) {
        databaseActions.incrementPendingChanges();
      }
      for (const reason of reasons) {
        try {
          schedulePushAfterPending(reason);
        } catch (error) {
          registerPendingOpLogger.warn(
            `Falha ao agendar push apos mutacao estrutural (${reason}):`,
            error
          );
        }
      }

      return { templateVersion, fieldResults };
    } catch (error) {
      updateItemLogger.error('Erro ao aplicar mutacao estrutural atomica no Dexie:', error);
      throw error;
    }
  }

  /**
   * Remove um item de uma coleção
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param id ID do item
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns Promise com boolean indicando sucesso
   */
  async deleteItem(collection: string, id: string, options: DatabaseMutationInput = false): Promise<boolean> {
    try {
      this.validateReady();
      const pendingOrigin = capturePendingWorkOrigin();

      const mutation = resolveDatabaseMutationOptions(options);
      const skipPendingOperation = mutation.skipPendingOperation;
      const remoteWrite = skipPendingOperation && mutation.source === 'remote';

      deleteItemLogger.debug(`Removendo item da coleção "${collection}" com ID "${id}"`);

      // Obter a tabela correspondente
      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      // Verificar se o item existe e obter uma cópia antes de excluir
      const item = await table.get(id);
      if (!item) {
        deleteItemLogger.warn(`Item não encontrado para exclusão na coleção "${collection}" com ID "${id}"`);
        return false;
      }

      if (remoteWrite) {
        await this.runMutationTransaction(table, async () => { await table.delete(id); });
      } else {
        await this.runMutationTransaction(table, async () => {
          await table.delete(id);
          if (!skipPendingOperation) {
            await this.registerPendingOperation('delete', collection, id, JSON.stringify({ ...item, ...(mutation.expectedPartnerRevision !== undefined ? { expected_partner_revision: mutation.expectedPartnerRevision } : {}) }), pendingOrigin);
          }
        });
      }

      if (skipPendingOperation) {
        deleteItemLogger.debug(`Pulando registro de operação pendente para exclusão ${collection}/${id}`);
      }

      deleteItemLogger.info(`Item removido com sucesso da coleção "${collection}" com ID "${id}"`);
      return true;
    } catch (error) {
      deleteItemLogger.error(`Erro ao remover item da coleção "${collection}" com ID "${id}":`, error);
      throw error;
    }
  }

  /**
   * Lista itens de uma coleção, opcionalmente com filtros
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param query Objeto de consulta para filtrar os resultados
   * @returns Promise com array de itens encontrados
   */
  async listItemsByIdPage<T>(collection: string, afterId: string, limit: number, metadata?: CanonicalMetadataCandidateScope): Promise<T[]> {
    this.validateReady();
    const table = this.getTable(collection);
    if (!table) throw new Error(`Tabela ${collection} não encontrada`);
    let page = table.where(':id').above(afterId);
    if (metadata) {
      if (!['guincho_checklists', 'guincho_valores_checklist'].includes(collection)) throw new Error('canonical_metadata_table_invalid');
      if (collection === 'guincho_valores_checklist' && !metadata.historicalEnabled) return [];
      const missing = (value: unknown) => value == null || value === '';
      // O cursor descarta linhas completas antes do limite, sem materializar o acervo em um array.
      page = page.filter(row => row.empresa_id === metadata.companyId && (collection === 'guincho_checklists'
        ? row.retired_at == null && (missing(row.sequencial_numero) || missing(row.sequencial_codigo)
          || (metadata.historicalEnabled && row.snapshot_id == null))
        : missing(row.snapshot_field_id) && Boolean(row.campo_id)));
    }
    return page.limit(Math.max(1, Math.min(100, limit))).toArray() as Promise<T[]>;
  }

  async listItems<T>(collection: string, query?: any): Promise<T[]> {
    try {
      this.validateReady();

      const detalhes: string[] = [];
      const avisos: string[] = [];
      const formatarValor = (valor: any): string => {
        if (valor === null || valor === undefined) {
          return String(valor);
        }
        if (typeof valor === 'string') {
          return valor.length > 60 ? `${valor.slice(0, 57)}...` : valor;
        }
        if (typeof valor === 'number' || typeof valor === 'boolean') {
          return String(valor);
        }
        try {
          const json = JSON.stringify(valor);
          return json.length > 80 ? `${json.slice(0, 77)}...` : json;
        } catch {
          return '[objeto]';
        }
      };
      const normalizarErro = (erro: unknown): string => {
        if (erro instanceof Error) return erro.message;
        if (typeof erro === 'string') return erro;
        try {
          return JSON.stringify(erro);
        } catch {
          return 'erro desconhecido';
        }
      };
      const adicionarDetalhe = (texto: string) => {
        if (texto && !detalhes.includes(texto)) {
          detalhes.push(texto);
        }
      };
      const adicionarAviso = (texto: string) => {
        if (texto) {
          avisos.push(texto);
        }
      };

      const table = this.getTable(collection);
      if (!table) {
        throw new Error(`Tabela ${collection} não encontrada`);
      }

      const { limit, offset, orderBy, ...whereFilters } = query || {};
      const possuiFiltros = whereFilters && Object.keys(whereFilters).length > 0;
      const filtrosAplicados = possuiFiltros
        ? Object.entries(whereFilters).map(([key, value]) => `${key}=${formatarValor(value)}`).join(', ')
        : 'nenhum';
      let ordenacaoDescricao = orderBy && typeof orderBy === 'string' ? orderBy.trim() : 'não informada';
      const paginacaoDescricao = limit || offset ? `offset=${offset ?? 0}, limit=${limit ?? 'todos'}` : 'não aplicada';

      let dexieQuery: any = table;

      if (possuiFiltros) {
        try {
          const key = Object.keys(whereFilters)[0];
          const value = whereFilters[key];
          const isOperatorObject = value && typeof value === 'object' && !Array.isArray(value);
          const indexes = table.schema.indexes.map(idx =>
            idx.keyPath ? idx.keyPath.toString() : ''
          );
          const primaryKeyPath = table.schema.primKey.keyPath
            ? table.schema.primKey.keyPath.toString()
            : 'id';

          if (!isOperatorObject && (key === primaryKeyPath || indexes.includes(key))) {
            adicionarDetalhe(`Filtro indexado: ${key} === ${formatarValor(value)}`);
            dexieQuery = table.where(key).equals(value);
          } else {
            adicionarDetalhe(`Filtro in-memory: ${key} (${formatarValor(value)})`);
            dexieQuery = table.toCollection();
          }
        } catch (whereError) {
          adicionarAviso(`Falha ao utilizar índice: ${normalizarErro(whereError)}`);
          dexieQuery = table.toCollection();
        }
      } else {
        adicionarDetalhe('Sem filtros WHERE');
        dexieQuery = table.toCollection();
      }

      if (orderBy && typeof orderBy === 'string') {
        const orderParts = orderBy.trim().split(' ');
        const orderField = orderParts[0];
        const orderDirection = orderParts[1]?.toUpperCase() === 'DESC' ? 'desc' : 'asc';

        const tableDefinitions = APP_CONSTANTS.DATABASE.TABLES;
        const tableDef = Object.values(tableDefinitions).find(def => def.NAME === collection);

        if (tableDef) {
          const allowedFields = tableDef.COLUMNS.map((col: any) => col.name);

          if (allowedFields.includes(orderField)) {
            adicionarDetalhe(`Ordenação validada: ${orderField} ${orderDirection.toUpperCase()}`);

            if (orderDirection === 'desc' && dexieQuery.reverse) {
              dexieQuery = dexieQuery.reverse();
            }

            try {
              dexieQuery = dexieQuery.sortBy(orderField);
            } catch (sortError) {
              adicionarAviso(`Erro ao ordenar por ${orderField}: ${normalizarErro(sortError)}`);
            }
          } else {
            adicionarAviso(`Campo de ordenação '${orderField}' não permitido. Campos válidos: ${allowedFields.join(', ')}`);
            if (allowedFields.includes('created_at')) {
              try {
                dexieQuery = dexieQuery.reverse().sortBy('created_at');
                ordenacaoDescricao = 'created_at DESC (padrão)';
                adicionarDetalhe('Ordenação padrão aplicada: created_at DESC');
              } catch (sortError) {
                adicionarAviso(`Erro ao aplicar ordenação padrão: ${normalizarErro(sortError)}`);
              }
            }
          }
        } else {
          adicionarAviso(`Definição da tabela ${collection} não encontrada para validar orderBy`);
        }
      }

      let items: any[];
      if (dexieQuery.toArray) {
        items = await dexieQuery.toArray();
      } else {
        items = await dexieQuery;
      }

      const totalAntesFiltroMemoria = items.length;
      let itensRemovidosInMemory = 0;
      const exemplosRemovidos: string[] = [];

      if (possuiFiltros) {
        items = items.filter(item => {
          const manter = Object.entries(whereFilters).every(([key, value]) => {
            if (value === null || value === undefined) return true;

            if (value && typeof value === 'object' && !Array.isArray(value)) {
              const v = (item as any)[key];
              const normalize = (val: any): number | string => {
                if (typeof val === 'number') return val;
                if (typeof val === 'string') {
                  const num = Number(val);
                  if (!isNaN(num)) return num;
                  const parsed = Date.parse(val);
                  if (!isNaN(parsed)) return parsed;
                }
                return val;
              };

              const leftVal = normalize(v);

              const result = Object.entries(value).every(([op, opVal]) => {
                const rightVal = normalize(opVal);
                switch (op) {
                  case '$eq':
                    return leftVal === rightVal;
                  case '$ne':
                    return leftVal !== rightVal;
                  case '$lt':
                    return (leftVal as any) < rightVal;
                  case '$lte':
                    return (leftVal as any) <= rightVal;
                  case '$gt':
                    return (leftVal as any) > rightVal;
                  case '$gte':
                    return (leftVal as any) >= rightVal;
                  default:
                    return false;
                }
              });

              if (!result) {
                itensRemovidosInMemory += 1;
                if (exemplosRemovidos.length < 3) {
                  const itemId = (item as any)?.id ?? 'sem-id';
                  exemplosRemovidos.push(String(itemId));
                }
              }
              return result;
            }

            const corresponde = (item as any)[key] === value;
            if (!corresponde) {
              itensRemovidosInMemory += 1;
              if (exemplosRemovidos.length < 3) {
                const itemId = (item as any)?.id ?? 'sem-id';
                exemplosRemovidos.push(String(itemId));
              }
            }
            return corresponde;
          });

          return manter;
        });
      }

      if (itensRemovidosInMemory > 0) {
        const exemplos =
          exemplosRemovidos.length > 0 ? ` (ex.: ${exemplosRemovidos.join(', ')})` : '';
        adicionarDetalhe(`Filtros em memória removeram ${itensRemovidosInMemory} itens${exemplos}`);
      }

      const totalAposFiltros = items.length;
      const startIndex = offset || 0;
      const endIndex = limit ? startIndex + limit : items.length;
      const paginatedItems = items.slice(startIndex, endIndex);
      const retornados = paginatedItems.length;

      const detalhesRelevantes = detalhes.filter(detail => detail !== 'Sem filtros WHERE');
      const deveLogar =
        avisos.length > 0 ||
        detalhesRelevantes.length > 0 ||
        totalAposFiltros > 0 ||
        retornados > 0;

      if (deveLogar) {
        const mensagemBase = [
          `Coleção: ${collection}`,
          `Filtros: ${filtrosAplicados}`,
          `Ordenação: ${ordenacaoDescricao}`,
          `Paginação: ${paginacaoDescricao}`,
          `Retorno: ${retornados}/${totalAposFiltros} itens`
        ].join(' | ');

        const detalhesTexto = detalhesRelevantes.length > 0 ? ` | Detalhes: ${detalhesRelevantes.join(' / ')}` : '';
        if (avisos.length > 0) {
          const avisosTexto = ` | Avisos: ${avisos.join(' / ')}`;
          listItemsLogger.warn(`${mensagemBase}${detalhesTexto}${avisosTexto}`);
        } else {
          listItemsLogger.info(`${mensagemBase}${detalhesTexto}`);
        }
      }

      return paginatedItems as unknown as T[];
    } catch (error) {
      listItemsLogger.error(`Erro ao listar itens da coleção "${collection}":`, error);
      throw error;
    }
  }

  /**
   * Registra uma operação pendente para sincronização posterior
   * @param operation Tipo de operação ('create', 'update', 'delete' | 'checklist_partial_update')
   * @param collection Nome da coleção (deve ser o nome da tabela, ex: "app_subscriptions")
   * @param id ID do item afetado
   * @param data Dados relacionados à operação (opcional)
   * @returns Promise com o ID da operação pendente
   */
  async registerPendingOperation(
    operation: 'create' | 'update' | 'delete' | 'checklist_partial_update',
    collection: string,
    id: string,
    data?: any,
    origin: PendingWorkOrigin | null = capturePendingWorkOrigin()
  ): Promise<string> {
    this.validateReady();
    return this.runMutationTransaction(this.db!.pending_operations, () =>
      this.registerPendingOperationWithinTransaction(operation, collection, id, data, origin));
  }

  private async registerPendingOperationWithinTransaction(
    operation: 'create' | 'update' | 'delete' | 'checklist_partial_update',
    collection: string,
    id: string,
    data?: any,
    origin: PendingWorkOrigin | null = capturePendingWorkOrigin()
  ): Promise<string> {
    try {
      this.validateReady();

      registerPendingOpLogger.debug(`Registrando operação pendente: ${operation} para ${collection}/${id}`);

      const triggerAutoPush = () => {
        try {
          schedulePushAfterPending(`${collection}:${operation}`);
        } catch (error) {
          registerPendingOpLogger.warn(`Falha ao agendar push automático após registrar pendência (${collection}:${operation}):`, error);
        }
      };

      // Inspecionar dados originais antes de limpar
      if (data) {
        try {
          let parsedData;
          if (typeof data === 'string') {
            parsedData = JSON.parse(data);
          } else {
            parsedData = data;
          }

          // Verificar e logar campos local_path
          const localPathFields = Object.keys(parsedData).filter(key => key.includes('local_path'));
          if (localPathFields.length > 0) {
            registerPendingOpLogger.info(`Campos local_path encontrados antes da limpeza: ${localPathFields.join(', ')}`);
            localPathFields.forEach(field => {
              registerPendingOpLogger.debug(`Campo ${field} valor: ${parsedData[field]}`);
            });
          }

          if (parsedData && parsedData.updated_at !== undefined) {
            const updatedAtValue = parsedData.updated_at;
            registerPendingOpLogger.info(`OPERAÇÃO PENDENTE: Campo updated_at presente nos dados com valor "${updatedAtValue}" (tipo: ${typeof updatedAtValue})`);
          }

          // Remover o campo user_id se ele não existir na definição da tabela
          if (parsedData.user_id !== undefined && !this.columnExistsInTable(collection, 'user_id')) {
            registerPendingOpLogger.debug(`Removendo campo user_id dos dados para a tabela ${collection} que não possui esta coluna`);
            delete parsedData.user_id;

            // Se os dados foram modificados, atualizar a string data
            if (typeof data === 'string') {
              data = JSON.stringify(parsedData);
            } else {
              data = parsedData;
            }
          }
        } catch (parseError) {
          registerPendingOpLogger.warn(`Não foi possível analisar dados para log: ${parseError}`);
        }
      }

      // Limpar campos local_path dos dados
      const cleanData = removeLocalPathFields(data);

      // Logar após a limpeza para verificar
      if (data && cleanData) {
        try {
          let originalObj = typeof data === 'string' ? JSON.parse(data) : data;
          let cleanObj = typeof cleanData === 'string' ? JSON.parse(cleanData) : cleanData;

          const originalKeys = Object.keys(originalObj);
          const cleanKeys = Object.keys(cleanObj);
          const removedKeys = originalKeys.filter(key => !cleanKeys.includes(key));

          if (removedKeys.length > 0) {
            registerPendingOpLogger.info(`Campos removidos na limpeza: ${removedKeys.join(', ')}`);
          } else {
            registerPendingOpLogger.debug('Nenhum campo local_path encontrado para remoção');
          }
        } catch (parseError) {
          registerPendingOpLogger.warn(`Erro ao analisar dados após limpeza: ${parseError}`);
        }
      }

      // Gerar ID para a operação pendente
      const operationId = uuidv4();
      const timestamp = Date.now();
      const dataJson = cleanData ? (typeof cleanData === 'string' ? cleanData : JSON.stringify(cleanData)) : undefined;

      // Consolidar múltiplas operações UPDATE (mesmo item) substituindo dados anteriores
      if (operation === 'update' && origin) {
        const existingUpdate = (await this.db!.pending_operations.toArray()).find(row =>
          row.operation === 'update' && row.collection === collection && row.item_id === id &&
          row.user_id === origin.userId && row.empresa_id === origin.empresaId);

        if (existingUpdate) {
          await this.db!.pending_operations.update(existingUpdate.id, {
            data: dataJson,
            timestamp,
          });
          registerPendingOpLogger.info(`Operação update existente atualizada: ${existingUpdate.id}`);
          triggerAutoPush();
          return existingUpdate.id;
        }
      }

      // Para checklist_partial_update, verificar se já existe operação pendente para o mesmo checklist e fazer upsert
      if (operation === 'checklist_partial_update' && origin) {
        const existingOp = (await this.db!.pending_operations.toArray()).find(row =>
          row.operation === 'checklist_partial_update' && row.collection === collection && row.item_id === id &&
          row.user_id === origin.userId && row.empresa_id === origin.empresaId);

        if (existingOp) {
          // Mesclar dados existentes com novos dados
          let mergedData = {};
          try {
            // Parse dos dados existentes
            if (existingOp.data) {
              mergedData = JSON.parse(existingOp.data);
            }
            // Parse dos novos dados e mesclar
            if (dataJson) {
              const newData = JSON.parse(dataJson);
              mergedData = mergeChecklistAnswerChanges(mergedData, newData);
            }
          } catch (error) {
            if (error instanceof Error && error.message === 'partner_pending_revision_mismatch') throw error;
            registerPendingOpLogger.warn(`Erro ao mesclar dados de pending operations: ${error}`);
            // Em caso de erro, usar apenas os novos dados
            mergedData = dataJson ? JSON.parse(dataJson) : {};
          }

          const mergedDataJson = JSON.stringify(mergedData);

          // Atualizar operação existente com dados mesclados e timestamp
          await this.db!.pending_operations.update(existingOp.id, {
            data: mergedDataJson,
            timestamp
          });
          registerPendingOpLogger.info(`Operação pendente atualizada com ID "${existingOp.id}" - dados mesclados: ${mergedDataJson}`);
          triggerAutoPush();
          return existingOp.id;
        }
      }

      // Adicionar na tabela de operações pendentes
      await this.db!.pending_operations.add({
        id: operationId,
        operation,
        collection,
        item_id: id,
        data: dataJson,
        timestamp,
        user_id: origin?.userId ?? null,
        empresa_id: origin?.empresaId ?? null,
      });

      // Atualizar contagem de operações pendentes no estado global (só para novas operações)
      databaseActions.incrementPendingChanges();
      triggerAutoPush();

      registerPendingOpLogger.info(`Operação pendente registrada com ID "${operationId}"`);
      return operationId;
    } catch (error) {
      registerPendingOpLogger.error(`Erro ao registrar operação pendente:`, error);
      throw error;
    }
  }

  /**
   * Busca operações pendentes para sincronização
   * @returns Promise com array de operações pendentes
   */
  async getPendingOperations(): Promise<PendingOperationRaw[]> {
    try {
      this.validateReady();
      // Buscar as pendências da tabela
      const result = await this.db!.table('pending_operations').toArray();
      // Garantir que cada objeto tenha item_id e operation do union correto
      return result.map((op: any) => ({
        id: op.id,
        operation: op.operation,
        collection: op.collection,
        item_id: op.item_id ?? op.itemId, // garantir compatibilidade
        data: op.data,
        timestamp: op.timestamp,
        user_id: op.user_id ?? null,
        empresa_id: op.empresa_id ?? null,
      })) as PendingOperationRaw[];
    } catch (error) {
      getPendingOpsLogger.error('Erro ao buscar operações pendentes:', error);
      throw error;
    }
  }

  /**
   * Remove uma operação pendente após sincronização bem-sucedida
   * @param id ID da operação pendente
   * @returns Promise com boolean indicando sucesso
   */
  async removePendingOperation(id: string): Promise<boolean> {
    try {
      this.validateReady();

      removePendingOpLogger.debug(`Removendo operação pendente com ID "${id}"`);

      // Verificar se a operação existe
      const operation = await this.db!.pending_operations.get(id);
      if (!operation) {
        removePendingOpLogger.warn(`Operação pendente com ID "${id}" não encontrada`);
        return false;
      }

      // Remover a operação
      await this.runMutationTransaction(this.db!.pending_operations, async () => { await this.db!.pending_operations.delete(id); });

      // Atualizar contagem de operações pendentes no estado global
      databaseActions.decrementPendingChanges();

      removePendingOpLogger.info(`Operação pendente com ID "${id}" removida com sucesso`);
      return true;
    } catch (error) {
      removePendingOpLogger.error(`Erro ao remover operação pendente com ID "${id}":`, error);
      throw error;
    }
  }

  async removePendingOperationIfUnchanged(
    id: string,
    expected: Pick<PendingOperationRaw, 'operation' | 'collection' | 'item_id' | 'data' | 'timestamp' | 'user_id' | 'empresa_id'>,
    canAcknowledge?: () => boolean
  ): Promise<boolean> {
    try {
      this.validateReady();

      let removed = false;
      await this.runTransaction('rw', this.db!.pending_operations, async () => {
        const operation = await this.db!.pending_operations.get(id);
        if (
          !operation ||
          operation.operation !== expected.operation ||
          operation.collection !== expected.collection ||
          operation.item_id !== expected.item_id ||
          operation.data !== expected.data ||
          operation.timestamp !== expected.timestamp ||
          (operation.user_id ?? null) !== (expected.user_id ?? null) ||
          (operation.empresa_id ?? null) !== (expected.empresa_id ?? null)
        ) {
          return;
        }

        if (canAcknowledge && !canAcknowledge()) return;

        await this.db!.pending_operations.delete(id);
        removed = true;
      });

      if (removed) {
        databaseActions.decrementPendingChanges();
        removePendingOpLogger.info(`Operação pendente com ID "${id}" removida condicionalmente`);
        return true;
      }

      removePendingOpLogger.warn(`Operação pendente com ID "${id}" mudou antes da remoção condicional`);
      return false;
    } catch (error) {
      removePendingOpLogger.error(`Erro ao remover operação pendente condicional com ID "${id}":`, error);
      throw error;
    }
  }

  async updateItemIfUnchanged(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    expected: Record<string, unknown>,
    skipPendingOperation: boolean = false,
    canAcknowledge?: () => boolean,
    guard?: HistoricalInsertGuard
  ): Promise<boolean> {
    this.validateReady();
    const pendingOrigin = capturePendingWorkOrigin();
    const table = this.getTable(collection);
    const pendingTable = this.db!.pending_operations;
    if (!table) throw new Error(`Tabela "${collection}" não encontrada`);

    let updated = false;
    const guardTables = [...new Set([...(guard?.rows.map(row => row.table) ?? []), ...(guard?.tables.map(row => row.table) ?? [])])].map(name => this.db!.table(name));
    await this.runTransaction('rw', [table, pendingTable,
      ...(skipPendingOperation && collection === 'guincho_valores_checklist' ? [this.db!.pending_images] : []), ...guardTables], async () => {
      if (guard && !await historicalInsertGuardMatches(guard,
        async (name, key) => await this.db!.table(name).get(key) ?? null,
        name => this.db!.table(name).toArray())) return;
      const current = await table.get(id) as Record<string, unknown> | undefined;
      if (!current || this.recordHasLocalChange(current, expected)) return;
      if (canAcknowledge && !canAcknowledge()) return;
      if (skipPendingOperation && collection === 'guincho_valores_checklist' && remoteWouldReplacePendingMedia(
        current, updates,
        await this.db!.pending_images.where('item_id').equals(current.checklist_id as string).toArray(),
        await pendingTable.where('item_id').equals(current.checklist_id as string).toArray()
      )) return;
      const affectedRows = await table.update(id, updates);
      if (guard && !guard.canApply()) throw new Error('historical_update_session_changed');
      updated = affectedRows > 0;
      if (updated && !skipPendingOperation) {
        const stored = await table.get(id) as Record<string, unknown> | undefined;
        await this.upsertStructuralPending(
          pendingTable,
          'update',
          collection,
          id,
          stored ?? { ...current, ...updates },
          Date.now(),
          pendingOrigin
        );
      }
    });
    return updated;
  }

  async deleteItemIfUnchanged(
    collection: string,
    id: string,
    expected: Record<string, unknown>,
    skipPendingOperation: boolean = false,
    canAcknowledge?: () => boolean
  ): Promise<boolean> {
    this.validateReady();
    const pendingOrigin = capturePendingWorkOrigin();
    const table = this.getTable(collection);
    const pendingTable = this.db!.pending_operations;
    if (!table) throw new Error(`Tabela "${collection}" não encontrada`);

    let deleted = false;
    await this.runTransaction('rw', [table, pendingTable], async () => {
      const current = await table.get(id) as Record<string, unknown> | undefined;
      if (!current || this.recordHasLocalChange(current, expected)) return;
      if (!skipPendingOperation) {
        await this.upsertStructuralPending(pendingTable, 'delete', collection, id, {}, Date.now(), pendingOrigin);
      }
      if (canAcknowledge && !canAcknowledge()) {
        if (!skipPendingOperation) throw new Error('session_changed_before_conditional_delete');
        return;
      }
      await table.delete(id);
      if (canAcknowledge && !canAcknowledge()) throw new Error('session_changed_after_conditional_delete');
      deleted = true;
    });
    return deleted;
  }

  async captureRecoverySnapshot(
    input: RecoveryCaptureInput
  ): Promise<RecoveryConsistentSnapshot> {
    this.validateReady();
    const tables = Object.values(RECOVERY_LOCAL_TABLE_NAMES).map(tableName => {
      const table = this.getTable(tableName);
      if (!table) throw new Error(`Tabela de recovery indisponivel: ${tableName}.`);
      return table;
    });

    return this.runTransaction('r', tables, async () => {
      const raw = {} as Record<RecoverySnapshotTableName, RecoverySnapshotRow[]>;
      for (const [snapshotName, tableName] of Object.entries(RECOVERY_LOCAL_TABLE_NAMES)) {
        raw[snapshotName as RecoverySnapshotTableName] = await this.getTable(tableName)!.toArray();
      }
      return scopeRecoverySnapshot(raw, input);
    });
  }

  async captureFullStateSnapshot(companyId: string, userId: string): Promise<SupportFullStateSnapshot> {
    this.validateReady();
    const tables = FULL_STATE_LOCAL_TABLE_NAMES.map(tableName => {
      const table = this.getTable(tableName);
      if (!table) throw new Error(`Tabela de suporte completa indisponivel: ${tableName}.`);
      return table;
    });
    return this.runTransaction('r', tables, async () => {
      const raw: Record<string, SupportFullStateRow[]> = {};
      for (const tableName of FULL_STATE_LOCAL_TABLE_NAMES) {
        raw[tableName] = await this.getTable(tableName)!.toArray() as SupportFullStateRow[];
      }
      return scopeFullStateSnapshot(raw, companyId, userId);
    });
  }

  async createChecklistSnapshotAndPending(input: ChecklistSnapshotAtomicCreateInput): Promise<void> {
    this.validateReady();
    if (input.snapshotFields.records.length === 0) {
      throw new Error('Transacao atomica exige campos de snapshot.');
    }

    const checklistTable = this.getTable(input.checklist.collection);
    const schemaVersionTable = input.schemaVersion
      ? this.getTable(input.schemaVersion.collection)
      : null;
    const snapshotTable = this.getTable(input.snapshot.collection);
    const snapshotFieldsTable = this.getTable(input.snapshotFields.collection);
    const pendingTable = this.db!.pending_operations;
    if (!checklistTable || (input.schemaVersion && !schemaVersionTable) || !snapshotTable || !snapshotFieldsTable) {
      throw new Error('Tabela indisponivel para transacao atomica de checklist.');
    }

    let insertedPending = false;
    const transactionTables: Dexie.Table<any, string>[] = [
      checklistTable,
      snapshotTable,
      snapshotFieldsTable,
      pendingTable,
    ];
    if (schemaVersionTable) transactionTables.unshift(schemaVersionTable);
    await this.runTransaction(
      'rw',
      transactionTables,
      async () => {
        if (input.schemaVersion && schemaVersionTable) {
          await this.putRecordIdempotently(
            schemaVersionTable,
            input.schemaVersion.collection,
            input.schemaVersion.record
          );
        }
        await this.putRecordIdempotently(checklistTable, input.checklist.collection, input.checklist.record);
        await this.putRecordIdempotently(snapshotTable, input.snapshot.collection, input.snapshot.record);
        for (const field of input.snapshotFields.records) {
          await this.putRecordIdempotently(snapshotFieldsTable, input.snapshotFields.collection, field);
        }

        const existingPending = await pendingTable
          .filter(operation => operation.operation === 'create'
            && operation.collection === input.pendingOperation.collection
            && operation.item_id === input.pendingOperation.itemId)
          .first();
        if (existingPending) {
          if (existingPending.data !== input.pendingOperation.data) {
            throw new Error('Pendencia create existente diverge da transacao atomica.');
          }
          return;
        }

        const pendingById = await pendingTable.get(input.pendingOperation.id);
        if (pendingById) {
          const same = pendingById.operation === 'create'
            && pendingById.collection === input.pendingOperation.collection
            && pendingById.item_id === input.pendingOperation.itemId
            && pendingById.data === input.pendingOperation.data;
          if (!same) throw new Error('Id de pendencia conflita com outra operacao local.');
          return;
        }

        await pendingTable.add({
          id: input.pendingOperation.id,
          operation: 'create',
          collection: input.pendingOperation.collection,
          item_id: input.pendingOperation.itemId,
          data: input.pendingOperation.data,
          timestamp: input.pendingOperation.timestamp,
          user_id: this.userId || '',
        });
        insertedPending = true;
      }
    );

    if (insertedPending) {
      databaseActions.incrementPendingChanges();
      try {
        schedulePushAfterPending(`${input.pendingOperation.collection}:create`);
      } catch (error) {
        registerPendingOpLogger.warn('Falha ao agendar push apos commit atomico de checklist:', error);
      }
    }
  }

  private assertCanonicalIdentity(
    collection: string,
    expected: Record<string, unknown>,
    current: Record<string, unknown>,
    keys: string[]
  ): void {
    for (const key of keys) {
      const expectedValue = expected[key] === undefined ? null : expected[key];
      const currentValue = current[key] === undefined ? null : current[key];
      if (expectedValue !== currentValue) {
        throw new Error(`Conflito local em ${collection}/${String(expected.id)}; reconciliacao recusada.`);
      }
    }
  }

  private async putCanonicalRecord(
    table: Dexie.Table<any, string>,
    collection: string,
    record: Record<string, unknown>,
    identityKeys: string[]
  ): Promise<void> {
    if (typeof record.id !== 'string' || record.id.length === 0) {
      throw new Error(`Registro canonico sem id para reconciliacao em ${collection}.`);
    }
    const current = await table.get(record.id) as Record<string, unknown> | undefined;
    if (current) this.assertCanonicalIdentity(collection, record, current, identityKeys);
    await table.put(record);
  }

  async reconcileChecklistSnapshotCanonical(
    input: ChecklistSnapshotCanonicalReconciliationInput
  ): Promise<void> {
    this.validateReady();
    if (input.snapshotFields.records.length === 0) {
      throw new Error('Reconciliacao atomica exige campos de snapshot.');
    }

    const schemaVersionTable = this.getTable(input.schemaVersion.collection);
    const checklistTable = this.getTable(input.checklist.collection);
    const snapshotTable = this.getTable(input.snapshot.collection);
    const snapshotFieldsTable = this.getTable(input.snapshotFields.collection);
    if (!schemaVersionTable || !checklistTable || !snapshotTable || !snapshotFieldsTable) {
      throw new Error('Tabela indisponivel para reconciliacao atomica de checklist.');
    }

    const transactionTables = [schemaVersionTable, checklistTable, snapshotTable, snapshotFieldsTable];
    await this.runTransaction('rw', transactionTables, async () => {
      await this.putCanonicalRecord(
        schemaVersionTable,
        input.schemaVersion.collection,
        input.schemaVersion.record,
        ['id', 'empresa_id', 'template_id', 'version', 'schema_payload']
      );

      const checklistId = input.checklist.record.id;
      const currentChecklist = typeof checklistId === 'string'
        ? await checklistTable.get(checklistId) as Record<string, unknown> | undefined
        : undefined;
      if (currentChecklist) {
        this.assertCanonicalIdentity(
          input.checklist.collection,
          input.checklist.record,
          currentChecklist,
          ['id', 'empresa_id', 'template_id', 'usuario_id']
        );
        const expectedChecklist = input.expectedChecklist;
        const preservesLocalChange = expectedChecklist
          && Object.entries(expectedChecklist).some(([key, value]) => {
            const expectedValue = value === undefined ? null : value;
            const currentValue = currentChecklist[key] === undefined ? null : currentChecklist[key];
            return expectedValue !== currentValue;
          });
        if (!preservesLocalChange) await checklistTable.put(input.checklist.record);
      } else {
        await checklistTable.put(input.checklist.record);
      }

      await this.putCanonicalRecord(
        snapshotTable,
        input.snapshot.collection,
        input.snapshot.record,
        ['id', 'empresa_id', 'checklist_id', 'template_id', 'schema_payload']
      );
      for (const field of input.snapshotFields.records) {
        await this.putCanonicalRecord(
          snapshotFieldsTable,
          input.snapshotFields.collection,
          field,
          [
            'id',
            'empresa_id',
            'snapshot_id',
            'original_campo_id',
            'field_key',
            'grupo',
            'nome',
            'tipo',
            'ordem',
            'obrigatorio',
            'visivel',
            'config',
          ]
        );
      }

      const provisionalId = input.provisionalSchemaVersionId;
      const canonicalId = input.schemaVersion.record.id;
      if (provisionalId && provisionalId !== canonicalId) {
        const stillReferenced = await snapshotTable
          .filter(row => row.template_schema_version_id === provisionalId)
          .first();
        if (!stillReferenced) await schemaVersionTable.delete(provisionalId);
      }
    });
  }

  /**
   * Fecha a conexão com o banco de dados
   */
  async close(): Promise<void> {
    try {
      closeLogger.info('Fechando conexão com o banco de dados Dexie');

      if (this.db) {
        // No Dexie, não precisamos fechar explicitamente a conexão, mas
        // podemos resetar nosso estado interno
        this.db.close();
        this.db = null;
        this.ready = false;

        databaseActions.setInitialized(false, null);
        closeLogger.info('Conexão com o banco de dados Dexie fechada');
      } else {
        closeLogger.warn('Tentativa de fechar conexão com o banco de dados que já está fechado');
      }
    } catch (error) {
      closeLogger.error('Erro ao fechar conexão com o banco de dados:', error);
      throw error;
    }
  }

  async commitArchivedPartialReplay(input: ArchivedReplayCommit): Promise<boolean> {
    this.validateReady();
    const names = ['accounted_pending_operations', 'guincho_checklists', 'guincho_valores_checklist', 'pending_operations', 'sync_conflicts'];
    const table = (name: string) => {
      if (!names.includes(name)) throw new Error('Tabela inválida no replay arquivado.');
      return this.getTable(name)!;
    };
    return this.runTransaction('rw', names.map(table), async () => commitArchivedPartialReplay(input, {
      get: (name, id) => table(name).get(id),
      list: (name, companyId, checklistId) => table(name).filter(row =>
        name === 'pending_operations' ? row.item_id === checklistId :
          row.empresa_id === companyId && (name === 'guincho_valores_checklist'
            ? row.checklist_id === checklistId : row.item_id === checklistId)).toArray(),
      insert: async (name, row) => { await table(name).add(row); },
      update: async (name, id, changes) => { await table(name).update(id, changes); },
      remove: async (name, id) => { await table(name).delete(id); },
    }));
  }

  async advanceChecklistMediaObservationBaseline(input: ChecklistMediaObservationBaselineInput): Promise<boolean> {
    this.validateReady();
    const values = this.getTable('guincho_valores_checklist')!;
    const images = this.getTable('pending_images')!;
    const parents = this.getTable('guincho_checklists')!;
    const tombstones = this.getTable('sync_tombstones')!;
    const operations = this.getTable('pending_operations')!;
    return this.runTransaction('rw', [values, images, parents, tombstones, operations, this.getTable('accounted_pending_operations')!, this.getTable('sync_conflicts')!], async () => {
      const pending = await images.get(input.pending.id);
      const value = await values.get(input.value.id);
      if (!pending || !value || this.recordHasLocalChange(pending, input.pending) ||
          this.recordHasLocalChange(value, input.value) || !input.canAcknowledge() ||
          !canAdvanceMediaObservationBaseline({ ...input, pending, value })) return false;
      if (hasMediaCancellation(await this.getTable('sync_conflicts')!.where('empresa_id').equals(pending.empresa_id).toArray(), pending)) return false;
      if (input.succession) {
        const archives = this.getTable('accounted_pending_operations')!;
        if (this.recordHasLocalChange(await archives.get(input.succession.archive.id), input.succession.archive) ||
            this.recordHasLocalChange(await archives.get(input.succession.evidence.id), input.succession.evidence)) return false;
      }
      const candidates = await images.where('item_id').equals(pending.item_id).and(row =>
        row.item_type === 'CHECKLIST' && row.campo_id === pending.campo_id &&
        row.empresa_id === pending.empresa_id && row.user_id === pending.user_id).toArray();
      const parent = await parents.get(pending.item_id);
      const ids = [value.id, pending.item_id, pending.campo_id, value.campo_id, value.snapshot_field_id, parent?.template_id].filter(Boolean);
      if (candidates.length !== 1 || !parent || parent.empresa_id !== pending.empresa_id || parent.retired_at ||
          await tombstones.where('empresa_id').equals(pending.empresa_id).and(row => ids.includes(row.entity_id)).count() ||
          await operations.where('operation').equals('delete').and(row => ids.includes(row.item_id)).count()) return false;
      await images.update(pending.id, { baseline_captured: 1,
        expected_remote_value: input.previousRemoteValue ?? null, expected_remote_updated_at: input.remoteUpdatedAt });
      if (!input.canAcknowledge()) throw new Error('session_changed_before_media_observation_ack');
      return true;
    });
  }

  async adoptChecklistMediaNoteCanonical(input: ChecklistMediaCanonicalAdoptionInput): Promise<boolean> {
    this.validateReady();
    const names = ['guincho_valores_checklist', 'pending_images', 'pending_operations', 'guincho_checklists',
      'guincho_checklist_snapshots', 'guincho_checklist_snapshot_fields', 'guincho_campos_template',
      'sync_conflicts', 'accounted_pending_operations', 'sync_tombstones'];
    const table = (name: string) => this.getTable(name)!;
    return this.runTransaction('rw', names.map(table), async () => {
      const plan = planChecklistMediaCanonicalAdoption(input, {
        operation: await table('pending_operations').get(input.operation.id), pending: await table('pending_images').get(input.pending.id),
        value: await table('guincho_valores_checklist').get(input.value.id), destination: await table('guincho_valores_checklist').get(input.remote.id),
        parent: await table('guincho_checklists').get(input.parent.id), snapshot: await table('guincho_checklist_snapshots').get(input.snapshot.id),
        field: await table('guincho_checklist_snapshot_fields').get(input.field.id),
        fields: await table('guincho_campos_template').where('id').equals(input.field.original_campo_id).toArray(),
        values: await table('guincho_valores_checklist').where('checklist_id').equals(input.parent.id).toArray(),
        images: await table('pending_images').where('item_id').equals(input.parent.id).toArray(),
        operations: await table('pending_operations').toArray(),
        conflicts: await table('sync_conflicts').where('empresa_id').equals(input.parent.empresa_id).toArray(),
        archives: await table('accounted_pending_operations').where('empresa_id').equals(input.parent.empresa_id).toArray(),
        tombstones: await table('sync_tombstones').where('empresa_id').equals(input.parent.empresa_id).toArray(),
      });
      if (!plan) return false;
      await table('guincho_valores_checklist').delete(input.value.id);
      await table('guincho_valores_checklist').add({ ...input.value, ...plan.valueChanges });
      await table('pending_images').update(input.pending.id, plan.pendingChanges);
      if (!input.canCommit()) throw new Error('session_changed_before_media_canonical_adoption');
      return true;
    });
  }

  async commitChecklistMediaConflict(input: ChecklistMediaConflictCommit): Promise<boolean> {
    this.validateReady();
    const tables = ['guincho_valores_checklist', 'pending_images', 'sync_conflicts',
      'accounted_pending_operations', 'pending_operations', 'guincho_checklists', 'sync_tombstones'];
    let removed = 0;
    const committed = await this.runTransaction('rw', tables.map(name => this.getTable(name)!), async () => {
      const read = (table: string, id: string) => this.getTable(table)!.get(id);
      const plan = planChecklistMediaConflictCommit(input, {
        archive: await read('accounted_pending_operations', input.archive.id),
        archives: await this.getTable('accounted_pending_operations')!.where('item_id').equals(input.pending.item_id).toArray(),
        conflict: await read('sync_conflicts', input.conflict.id),
        pending: await read('pending_images', input.pending.id),
        value: await read('guincho_valores_checklist', input.value.id),
        parent: await read('guincho_checklists', input.parent.id),
        images: await this.getTable('pending_images')!.where('item_id').equals(input.pending.item_id).toArray(),
        operations: await this.getTable('pending_operations')!.toArray(),
        tombstones: await this.getTable('sync_tombstones')!.where('empresa_id').equals(input.pending.empresa_id).toArray(),
        conflicts: await this.getTable('sync_conflicts')!.where('empresa_id').equals(input.pending.empresa_id).toArray(),
      });
      if (!plan) return false;
      if (plan.receipt) await this.getTable('sync_conflicts')!.put(plan.receipt);
      await this.getTable('guincho_valores_checklist')!.update(input.value.id, {
        valor: input.remote.valor, observacao: input.remote.observacao ?? null, updated_at: input.remote.updated_at,
        campo_id: input.remote.campo_id ?? null, snapshot_field_id: input.remote.snapshot_field_id ?? null });
      await this.getTable('accounted_pending_operations')!.update(input.archive.id, { data: plan.archiveData });
      for (const partial of plan.partials) {
        if (partial.data === null) { await this.getTable('pending_operations')!.delete(partial.id); removed += 1; }
        else await this.getTable('pending_operations')!.update(partial.id, { data: partial.data });
      }
      await this.getTable('pending_images')!.delete(input.pending.id);
      await this.getTable('sync_conflicts')!.update(input.conflict.id, { status: 'resolved', resolved_at: Date.now(), updated_at: Date.now() });
      if (!input.canCommit()) throw new Error('session_changed_before_media_conflict_commit');
      return true;
    });
    if (committed) for (let i = 0; i < removed; i += 1) databaseActions.decrementPendingChanges();
    return committed;
  }

  async finalizeChecklistMedia(input: ChecklistMediaFinalizationInput): Promise<boolean> {
    this.validateReady();
    const values = this.getTable('guincho_valores_checklist')!;
    const pending = this.getTable('pending_images')!;
    const receipts = this.getTable('sync_conflicts')!;
    const operations = this.getTable('pending_operations')!;
    let removedPartial = false;
    const committed = await this.runTransaction('rw', [values, pending, receipts, operations, this.getTable(DEFERRED_MEDIA_CLEANUP_TABLE)!, this.getTable('accounted_pending_operations')!, this.getTable('guincho_checklists')!, this.getTable('sync_tombstones')!], async () => {
      if (input.recoveryReceipt) {
        const receipt = await receipts.get(input.recoveryReceipt.id);
        if (!receipt || this.recordHasLocalChange(receipt, input.recoveryReceipt)) return false;
      }
      const queued = await pending.get(input.pending.id);
      const value = await values.get(input.value.id);
      if (!queued || !value || this.recordHasLocalChange(queued, input.pending) ||
          this.recordHasLocalChange(value, input.value) || !input.canAcknowledge()) return false;
      const partial = input.recoveryReceipt ? null : planAcknowledgedMediaPartial(
        await operations.where('item_id').equals(queued.item_id).and(row =>
          row.operation === 'checklist_partial_update' && row.collection === 'guincho_valores_checklist' &&
          row.user_id === queued.user_id && row.empresa_id === queued.empresa_id).toArray(),
        queued, input.publicUrl, input.expectedObservation
      );
      const observations = input.remoteObservation === undefined ? [] : await operations.where('item_id').equals(queued.item_id).toArray();
      const conflicts = await receipts.where('empresa_id').equals(queued.empresa_id).toArray();
      const successor = input.remoteObservation === undefined ? null : planSupersededMediaNoteConflict(queued, value,
        { ...value, valor: input.publicUrl, observacao: input.remoteObservation, updated_at: input.remoteUpdatedAt,
          campo_id: input.remoteCampoId === undefined ? value.campo_id : input.remoteCampoId,
          snapshot_field_id: input.remoteSnapshotFieldId ?? value.snapshot_field_id ?? null },
        await this.getTable('accounted_pending_operations')!.where('item_id').equals(queued.item_id).toArray(), conflicts,
        await operations.where('item_id').equals(queued.item_id).toArray(), await this.getTable('guincho_checklists')!.get(queued.item_id),
        await this.getTable('sync_tombstones')!.where('empresa_id').equals(queued.empresa_id).toArray(),
        await pending.where('item_id').equals(queued.item_id).toArray());
      if (input.remoteObservation !== undefined && hasOpenMediaObservation(
        conflicts.filter(row => row.id !== successor?.conflict.id), value, queued)) return false;
      if (successor) {
        await this.getTable('accounted_pending_operations')!.update(successor.archive.id, { data: successor.data });
        await receipts.update(successor.conflict.id, { status: 'resolved', resolved_at: Date.now(), updated_at: Date.now() });
      }
      const observation = input.remoteObservation === undefined || hasPendingMediaObservation(observations, value, queued)
        ? value.observacao ?? null : input.remoteObservation;
      await values.update(value.id, { valor: input.publicUrl, observacao: observation, updated_at: input.remoteUpdatedAt,
        snapshot_field_id: input.remoteSnapshotFieldId ?? value.snapshot_field_id ?? null,
        campo_id: input.remoteCampoId === undefined ? value.campo_id : input.remoteCampoId });
      if (partial) {
        if (partial.remainingData === null) {
          await operations.delete(partial.id);
          removedPartial = true;
        } else {
          await operations.update(partial.id, { data: partial.remainingData });
        }
      }
      await this.getTable(DEFERRED_MEDIA_CLEANUP_TABLE)!.put(retainAcknowledgedLocalMedia(queued, value, input.publicUrl));
      await pending.delete(queued.id);
      if (input.recoveryReceipt) await receipts.update(input.recoveryReceipt.id, { status: 'resolved', resolved_at: Date.now(), updated_at: Date.now() });
      if (!input.canAcknowledge()) throw new Error('session_changed_before_media_finalization');
      return true;
    });
    if (committed && removedPartial) databaseActions.decrementPendingChanges();
    return committed;
  }

  async completeChecklistMedia(input: ChecklistMediaCompletionInput): Promise<boolean> {
    this.validateReady();
    if (!/^https?:\/\//.test(input.publicUrl)) throw new Error('URL remota inválida para confirmar mídia.');
    const collection = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
    const values = this.getTable(collection)!;
    const pending = this.getTable('pending_images')!;
    let pendingChange: StructuralPendingChange | null = null;
    const committed = await this.runTransaction('rw', [values, pending, this.db!.pending_operations], async () => {
      const queued = await pending.get(input.pendingId);
      if (!queued || queued.item_type !== 'CHECKLIST' || queued.item_id !== input.checklistId ||
          queued.campo_id !== input.campoId || queued.local_uri !== input.localUri ||
          queued.user_id !== input.userId || queued.empresa_id !== input.pendingEmpresaId) return false;
      const candidates = await values.where('checklist_id').equals(input.checklistId).and(row => row.empresa_id === input.empresaId &&
        (row.snapshot_field_id === input.campoId || (!row.snapshot_field_id && row.campo_id === input.campoId))).toArray();
      const value = candidates.find(row => row.snapshot_field_id === input.campoId) ?? candidates[0];
      if (!value || value.valor !== input.localUri) return false;
      if (input.canAcknowledge && !input.canAcknowledge()) return false;
      await values.update(value.id, { valor: input.publicUrl, updated_at: Date.now() });
      await pending.update(queued.id, { prepared_public_url: input.publicUrl });
      pendingChange = await this.upsertStructuralPending(this.db!.pending_operations, 'checklist_partial_update', collection,
        input.checklistId, { [input.campoId]: queued.expected_partner_revision != null ? { valor: input.publicUrl, expected_partner_revision: queued.expected_partner_revision } : input.publicUrl }, Date.now(),
        { userId: input.userId, empresaId: input.pendingEmpresaId });
      if (input.canAcknowledge && !input.canAcknowledge()) {
        throw new Error('session_changed_before_checklist_media_ack');
      }
      return true;
    });
    if (pendingChange === 'inserted') databaseActions.incrementPendingChanges();
    if (committed && pendingChange !== 'unchanged') {
      try { schedulePushAfterPending(`${collection}:checklist_partial_update`); }
      catch (error) { registerPendingOpLogger.warn('Falha ao agendar envio de mídia confirmada:', error); }
    }
    return committed;
  }

  async retainTombstonedChecklistUpload(input: RetainedChecklistUploadInput): Promise<boolean> {
    this.validateReady();
    const pending = this.getTable('pending_images')!;
    const retained = this.getTable(DEFERRED_MEDIA_CLEANUP_TABLE)!;
    return this.runTransaction('rw', [pending, retained], async () => {
      const current = await pending.get(input.pending.id);
      if (!current || this.recordHasLocalChange(current, input.pending) || !input.canCommit()) return false;
      if (current.item_type !== 'CHECKLIST' || (current.empresa_id && current.empresa_id !== input.companyId)) throw Error('retention_upload_scope');
      await retained.put({ id: `tombstone-upload:${current.id}`, user_id: current.user_id ?? 'unknown', empresa_id: input.companyId,
        checklist_id: current.item_id, field_id: current.campo_id, created_at: Date.now(),
        payload: JSON.stringify({ schema_version: 1, kind: 'tombstoned-upload', state: 'retained', physical_deletion_allowed: false, pending: current }) });
      await pending.delete(current.id);
      if (!input.canCommit()) throw Error('retention_session_changed');
      return true;
    });
  }

  async clearTablesIfUnchanged(expected: Record<string, Record<string, any>[]>, preserve: readonly string[], canClear?: () => boolean): Promise<boolean> {
    this.validateReady();
    const names = Object.keys(expected);
    const normalized = (rows: Record<string, any>[]) => JSON.stringify(rows.slice().sort((a, b) => String(a.id).localeCompare(String(b.id))));
    return this.runTransaction('rw', names.map(name => this.getTable(name)!), async () => {
      if (canClear && !canClear()) return false;
      for (const name of names) {
        if (normalized(await this.getTable(name)!.toArray()) !== normalized(expected[name])) return false;
      }
      for (const name of names) if (name !== 'local_database_control' && !preserve.includes(name)) await this.getTable(name)!.clear();
      if (canClear && !canClear()) throw new Error('reset_session_changed_before_commit');
      await this.db!.table('local_database_control').put({ id: 'generation', epoch: this.boundEpoch + 1 });
      return true;
    });
  }

  async deleteChecklistPreservingMedia(input: ChecklistDeletionInput): Promise<boolean> {
    this.validateReady();
    const result = await this.runTransaction('rw', CHECKLIST_DELETION_TABLES.map(name => this.getTable(name)!), () =>
      deleteChecklistInTransaction(input, {
        all: name => this.getTable(name)!.toArray(),
        put: async (name, row) => { await this.getTable(name)!.put(row); },
        remove: async (name, id) => { await this.getTable(name)!.delete(id); },
        queueDelete: async (name, row) => { await this.upsertStructuralPending(this.db!.pending_operations,
          'delete', name, row.id, { ...row, ...(input.expectedPartnerRevision !== undefined
            ? { expected_partner_revision: input.expectedPartnerRevision } : {}) }, Math.max(Date.now(), ...(await this.db!.pending_operations.toArray()).map(op => Number(op.timestamp) + 1)), input.origin);
          const operation = (await this.db!.pending_operations.toArray()).find(op => op.operation === 'delete' &&
            op.collection === name && op.item_id === row.id && op.user_id === input.origin.userId && op.empresa_id === input.origin.empresaId);
          if (!operation) throw new Error('checklist_deletion_outbox_missing');
          return operation.id; },
      }));
    if (result) {
      try {
        const pending = await this.getPendingOperations();
        if (input.canCommit()) databaseActions.updatePendingChanges(pending.length);
      } catch (error) { registerPendingOpLogger.warn('Contagem da fila será atualizada no próximo ciclo.', error); }
      try { schedulePushAfterPending('checklist-delete-with-retention'); }
      catch (error) { registerPendingOpLogger.warn('Exclusão gravada; agendamento será retomado.', error); }
    }
    return result;
  }

  async persistChecklistMediaPending(
    collection: string,
    rows: Record<string, any>[],
    media: ChecklistMediaPendingInput
  ): Promise<void> {
    this.validateReady();
    assertChecklistMediaPendingInput(collection, rows, media);
    const values = this.getTable(collection)!;
    const pending = this.getTable('pending_images')!;
    media.onStage?.('prepare_transaction');
    let logicalChange: StructuralPendingChange | undefined;
    await this.runTransaction('rw', [values, pending, this.getTable('guincho_checklists')!, this.getTable('guincho_checklist_snapshot_fields')!, this.getTable('sync_tombstones')!, this.getTable('pending_operations')!, this.getTable('accounted_pending_operations')!, this.getTable(DEFERRED_MEDIA_CLEANUP_TABLE)!], async () => {
      if (media.canAcknowledge && !media.canAcknowledge()) {
        throw new Error('session_changed_before_checklist_media_prepare');
      }
      if (media.pending &&
          (media.pending.user_id !== (media.pendingOrigin?.userId ?? null) ||
            media.pending.empresa_id !== (media.pendingOrigin?.empresaId ?? null))) {
        throw new Error('checklist_media_pending_origin_mismatch');
      }
      const incoming = rows[0];
      if (media.expectedValue !== undefined) {
        const parent = await this.getTable('guincho_checklists')!.get(media.checklistId);
        const ids = [media.checklistId, media.campoId, parent?.template_id].filter(Boolean);
        const removed = await this.getTable('sync_tombstones')!.where('empresa_id').equals(incoming.empresa_id)
          .and(row => ids.includes(row.entity_id)).count();
        if (!parent || !parent.template_id || parent.empresa_id !== incoming.empresa_id || parent.retired_at || removed) throw new Error('media_preparation_superseded');
      }
      const candidates = await values.where('checklist_id').equals(media.checklistId).and(row => row.empresa_id === incoming.empresa_id && (incoming.snapshot_field_id
          ? row.snapshot_field_id === incoming.snapshot_field_id
          : !row.snapshot_field_id && row.campo_id === incoming.campo_id)).toArray();
      if (candidates.length > 1) throw new Error('Checklist possui respostas locais ambíguas.');
      const existing = candidates[0] ?? await values.get(incoming.id);
      const canonicalHistoricalField = existing && existing.campo_id == null && incoming.campo_id && incoming.snapshot_field_id &&
        existing.snapshot_field_id === incoming.snapshot_field_id;
      if (canonicalHistoricalField) {
        const snapshotField = await this.getTable('guincho_checklist_snapshot_fields')!.get(incoming.snapshot_field_id);
        const parent = await this.getTable('guincho_checklists')!.get(media.checklistId);
        if (!snapshotField || snapshotField.empresa_id !== incoming.empresa_id || snapshotField.original_campo_id !== incoming.campo_id ||
            !parent || parent.empresa_id !== incoming.empresa_id || parent.retired_at || parent.snapshot_id !== snapshotField.snapshot_id) {
          throw new Error('Identidade histórica da resposta local diverge do snapshot/checklist.');
        }
      }
      if (existing && (existing.empresa_id !== incoming.empresa_id || existing.checklist_id !== incoming.checklist_id ||
          ((existing.campo_id ?? null) !== (incoming.campo_id ?? null) && !canonicalHistoricalField) ||
          (existing.snapshot_field_id != null && existing.snapshot_field_id !== incoming.snapshot_field_id))) {
        throw new Error('Identidade da resposta local diverge da empresa/checklist/campo/snapshot.');
      }
      if (media.expectedValue !== undefined && String(existing?.valor ?? '') !== media.expectedValue &&
          existing?.valor !== incoming.valor) throw new Error('media_preparation_superseded');
      const previousPending = media.pending && existing?.valor
        ? await pending.where('item_id').equals(media.checklistId).and(row => row.item_type === 'CHECKLIST' &&
          row.campo_id === media.campoId &&
          (row.local_uri === existing.valor || row.prepared_public_url === existing.valor) &&
          row.user_id === media.pending!.user_id && row.empresa_id === media.pending!.empresa_id).first()
        : null;
      const preparedPending = media.pending
        ? { ...media.pending, ...captureChecklistMediaBaseline(existing, previousPending),
          ...(previousPending?.id === media.pending.id ? { expected_partner_revision: previousPending.expected_partner_revision ?? null } : {}) } : null;
      media.onStage?.('prepare_value');
      const valueRow = existing
        ? { ...incoming, id: existing.id, created_at: existing.created_at,
          ...(existing.snapshot_field_id && existing.snapshot_field_id === incoming.snapshot_field_id
            ? { campo_id: existing.campo_id ?? null } : {}),
          ...(incoming.observacao === undefined && Object.prototype.hasOwnProperty.call(existing, 'observacao')
            ? { observacao: existing.observacao ?? null } : {}) }
        : incoming;
      const lineage = captureChecklistMediaSuccession(preparedPending, previousPending, existing,
        await this.getTable('pending_operations')!.where('item_id').equals(media.checklistId).toArray(),
        await this.getTable('accounted_pending_operations')!.where('item_id').equals(media.checklistId).toArray());
      const supersededUploads = media.pendingOrigin ? await pending.where('item_id').equals(media.checklistId).and(row =>
        row.item_type === 'CHECKLIST' && row.campo_id === media.campoId && row.id !== media.pending?.id &&
        row.user_id === media.pendingOrigin!.userId && row.empresa_id === media.pendingOrigin!.empresaId).toArray() : [];
      const cleanup = planDeferredMediaCleanup({ successor: media.pending, id: media.cleanupId ?? media.pending?.id, origin: media.pendingOrigin, fieldId: media.campoId,
        previous: existing ?? null, next: valueRow, supersededUploads });
      if (cleanup) {
        const table = this.getTable(DEFERRED_MEDIA_CLEANUP_TABLE)!;
        const previous = await table.get(cleanup.id);
        assertSameCleanupIntent(previous, cleanup);
        if (!previous) await table.add(cleanup);
        media.onStage?.('prepare_cleanup');
      }
      if (lineage) await this.getTable('accounted_pending_operations')!.add(lineage);
      await values.put(valueRow);
      media.onStage?.('prepare_pending');
      if (media.pendingOrigin) {
        await pending.where('item_id').equals(media.checklistId).and(row => row.item_type === 'CHECKLIST' &&
          row.campo_id === media.campoId && row.user_id === media.pendingOrigin!.userId &&
          row.empresa_id === media.pendingOrigin!.empresaId).delete();
      }
      if (preparedPending) await pending.put(preparedPending);
      if (media.logicalChanges) {
        logicalChange = await this.upsertStructuralPending(this.db!.pending_operations, 'checklist_partial_update', collection,
          media.checklistId, JSON.parse(media.logicalChanges), Date.now(), media.pendingOrigin);
        media.onStage?.('prepare_operation');
      }
      if (media.canAcknowledge && !media.canAcknowledge()) {
        throw new Error('session_changed_before_checklist_media_prepare_commit');
      }
      media.onStage?.('prepare_commit');
    });
    if (logicalChange === 'inserted') databaseActions.incrementPendingChanges();
    if (logicalChange && logicalChange !== 'unchanged') {
      try { schedulePushAfterPending(`${collection}:checklist_partial_update`); }
      catch (error) { console.warn('Não foi possível agendar envio após remoção local de mídia.', error); }
    }
  }

  async bulkUpsertWithPendingOperation(
    collection: string,
    rows: any[],
    pending: {
      operation: 'create' | 'update' | 'delete' | 'checklist_partial_update';
      itemId: string;
      data?: any;
      pendingOrigin?: PendingWorkOrigin | null;
      canAcknowledge?: () => boolean;
    }
  ): Promise<void> {
    this.validateReady();
    if (rows.length === 0) {
      throw new Error('Bulk upsert atomico exige ao menos um registro.');
    }

    const table = this.getTable(collection);
    if (!table) {
      throw new Error(`Tabela ${collection} não encontrada`);
    }

    const rowsWithId = rows.map((row) => ({ ...row, id: row.id || uuidv4() }));
    const pendingOrigin = pending.pendingOrigin !== undefined
      ? pending.pendingOrigin
      : capturePendingWorkOrigin();
    const stores = [table, this.db!.pending_operations];
    if (collection === 'guincho_valores_checklist') stores.push(this.db!.table('guincho_checklist_snapshot_fields'));
    await this.runTransaction('rw', stores, async () => {
      if (pending.canAcknowledge && !pending.canAcknowledge()) {
        throw new Error('session_changed_before_bulk_upsert');
      }
      if (collection === 'guincho_valores_checklist') {
        for (const row of rowsWithId) {
          const logicalExisting = row.empresa_id && row.checklist_id
            ? await table.where('checklist_id').equals(row.checklist_id).and((candidate: any) =>
                candidate.empresa_id === row.empresa_id && (row.snapshot_field_id
                  ? candidate.snapshot_field_id === row.snapshot_field_id
                  : !candidate.snapshot_field_id && candidate.campo_id === row.campo_id)).toArray()
            : [];
          if (logicalExisting.length > 1) throw new Error('Checklist possui respostas locais ambíguas.');
          const existing = logicalExisting[0] ?? await table.get(row.id);
          const snapshotField = existing && existing.campo_id == null && row.campo_id && row.snapshot_field_id && row.snapshot_field_id === existing.snapshot_field_id
            ? await this.db!.table('guincho_checklist_snapshot_fields').get(row.snapshot_field_id) : null;
          const canonicalHistoricalField = snapshotField?.empresa_id === row.empresa_id &&
            snapshotField?.original_campo_id === row.campo_id;
          if (existing && (existing.empresa_id !== row.empresa_id || existing.checklist_id !== row.checklist_id ||
              ((existing.campo_id ?? null) !== (row.campo_id ?? null) && !canonicalHistoricalField) ||
              (existing.snapshot_field_id != null && existing.snapshot_field_id !== row.snapshot_field_id))) {
            throw new Error('Identidade da resposta local diverge da empresa/checklist/campo/snapshot.');
          }
          if (existing) {
            row.id = existing.id;
            row.created_at = existing.created_at ?? row.created_at;
            if (existing.snapshot_field_id && existing.snapshot_field_id === row.snapshot_field_id) row.campo_id = existing.campo_id ?? null;
          }
          const mediaVersion = checklistObservationMediaVersion(row, existing);
          if (mediaVersion) row.updated_at = mediaVersion;
          if (row.valor === undefined) row.valor = existing?.valor ?? null;
          if (row.observacao === undefined) row.observacao = existing?.observacao ?? null;
        }
      }
      await table.bulkPut(rowsWithId);
      const pendingData = collection === 'guincho_valores_checklist'
        ? mergeChecklistAnswerValuesFromRows(pending.data, rowsWithId)
        : pending.data;
      await this.registerPendingOperation(
        pending.operation,
        collection,
        pending.itemId,
        pendingData,
        pendingOrigin
      );
      if (pending.canAcknowledge && !pending.canAcknowledge()) {
        throw new Error('session_changed_before_bulk_upsert_ack');
      }
    });
  }

  /**
   * Upsert em lote usando bulkPut do Dexie
   * @param collection Nome da tabela
   * @param rows Array de objetos a serem upsertados
   * @param skipPendingOperation Se true, não registra operações pendentes (sempre true para bulkUpsert)
   */
  async bulkUpsert(collection: string, rows: any[], skipPendingOperation: boolean = true): Promise<void> {
    if (rows.length === 0) {
      bulkUpsertLogger.info(`Bulk upsert ignorado para "${collection}" pois não há registros.`);
      return;
    }

    this.validateReady();
    const table = this.getTable(collection);
    if (!table) throw new Error(`Tabela ${collection} não encontrada`);

    const startTime = Date.now();
    const rowsWithId = rows.map(row => ({ ...row, id: row.id || uuidv4() }));
    const sampleIds = rowsWithId
      .slice(0, 3)
      .map((row: any) => row?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    bulkUpsertLogger.info(
      `Iniciando bulk upsert em "${collection}" com ${rowsWithId.length} registro(s)` +
      `${skipPendingOperation ? ' (sem registrar pendências)' : ''}` +
      `${sampleIds.length > 0 ? ` | amostra IDs: ${sampleIds.join(', ')}` : ''}`
    );

    if (collection === 'guincho_valores_checklist') {
      await this.runTransaction('rw', table, async () => {
        for (const row of rowsWithId) {
          const existing = await table.get(row.id);
          if (row.created_at === undefined && existing) row.created_at = existing.created_at;
          if (row.valor === undefined) row.valor = existing?.valor ?? null;
          if (row.observacao === undefined) row.observacao = existing?.observacao ?? null;
        }
        await table.bulkPut(rowsWithId);
      });
    } else {
      await this.runTransaction('rw', table, async () => { await table.bulkPut(rowsWithId); });
    }
    const duration = Date.now() - startTime;
    bulkUpsertLogger.info(`Bulk upsert concluído em "${collection}" | registros=${rowsWithId.length} | duração=${duration}ms`);
    await debugPause('bulkUpsert - após bulkPut');
  }

  async bulkInsertIfAbsent(collection: string, rows: any[], guard?: HistoricalInsertGuard): Promise<number> {
    if (rows.length === 0) return 0;

    this.validateReady();
    const table = this.getTable(collection);
    if (!table) throw new Error(`Tabela ${collection} não encontrada`);

    const uniqueRows = Array.from(
      new Map(rows.map(row => {
        if (!row?.id) throw new Error(`bulkInsertIfAbsent exige id em "${collection}"`);
        return [row.id, row];
      })).values()
    );
    let inserted = 0;
    const startTime = Date.now();

    const transactionTables = [table, ...new Set([
      ...(guard?.rows.map(row => row.table) ?? []), ...(guard?.tables.map(row => row.table) ?? []),
    ])].map(name => typeof name === 'string' ? this.db!.table(name) : name);
    await this.runTransaction('rw', transactionTables, async () => {
      if (guard && !await historicalInsertGuardMatches(guard,
        async (name, id) => await this.db!.table(name).get(id) ?? null,
        name => this.db!.table(name).toArray())) return;
      const existingRows = await table.bulkGet(uniqueRows.map(row => row.id));
      const missingRows = uniqueRows.filter((_row, index) => existingRows[index] === undefined);
      if (guard && !guard.canApply()) return;
      if (missingRows.length > 0) {
        await table.bulkAdd(missingRows);
        if (guard && !guard.canApply()) throw new Error('historical_insert_session_changed');
        inserted = missingRows.length;
      }
    });

    bulkUpsertLogger.info(
      `Bulk insert-if-absent concluído em "${collection}" | recebidos=${rows.length}` +
      ` | inseridos=${inserted} | preservados=${uniqueRows.length - inserted}` +
      ` | duração=${Date.now() - startTime}ms`
    );
    return inserted;
  }
}

export default DexieAdapter;
